// Flat dictionary of all user-facing strings, EN + FR.
// `fr` is typed `typeof en` so a missing translation is a compile error.
// Keep keys grouped by prefix (login., dashboard., …) for readability.

const en = {
  // ━━━ Help page (next-steps D) ━━━
  'help.topbar.title': 'Help',
  'help.topbar.subtitle': 'Everything you need to use SmartAnalyst',
  'help.eyebrow': 'Documentation',
  'help.title': 'Welcome to SmartAnalyst',
  'help.intro':
    "Concrete guides for everything you can do here: connect a source, ask a question, generate a report, pin a widget. Nothing more, nothing less — if you don't find an answer here, write to support.",
  'help.toc.title': 'In this page',
  'help.toc.aria': 'Table of contents',
  // Getting started
  'help.gs.title': 'Getting started in 3 steps',
  'help.gs.body1':
    'After signup, the fastest path to value is to connect one source and ask one question. Less than 5 minutes.',
  'help.gs.step1': 'Connect a source',
  'help.gs.step1.sub': 'GA4 is the most common starting point (OAuth, 30 seconds).',
  'help.gs.step2': 'Wait for the first brief',
  'help.gs.step2.sub': 'We sync 30 days of history. The home page lights up automatically.',
  'help.gs.step3': 'Ask your first question',
  'help.gs.step3.sub': 'Use the quick-cards on the chat to get a feel of what is possible.',
  // Sources
  'help.sources.title': 'Connected sources',
  'help.sources.body':
    'SmartAnalyst connects to your marketing tools via OAuth (or API token). All connectors are read-only — we never modify your data on the source side.',
  'help.sources.ga4': 'Web analytics (sessions, conversions, channels)',
  'help.sources.meta_ads': 'Facebook + Instagram Ads (spend, ROAS, campaigns)',
  'help.sources.google_ads': 'Search + Display Ads',
  'help.sources.stripe': 'Subscriptions, MRR, churn, revenue',
  'help.sources.search_console': 'Organic search (queries, CTR, position)',
  'help.sources.smarttag': '1st-party tracking installed via JS snippet',
  'help.sources.callout':
    'A connector failing? The "Sources" page shows the health of each connector with a one-click reauth.',
  // Chat
  'help.chat.title': 'The AI chat copilot',
  'help.chat.body':
    'The chat is not just text — it generates structured outputs (charts, tables, funnels) and lets you act on them (export, pin, slide deck).',
  'help.chat.outputs.title': 'Generative outputs',
  'help.chat.out.kpi': 'KPI card',
  'help.chat.out.kpi.desc': 'A figure + delta vs previous period, with a mini sparkline.',
  'help.chat.out.chart': 'Chart',
  'help.chat.out.chart.desc': 'Daily / weekly time series, bar or line.',
  'help.chat.out.table': 'Comparison table',
  'help.chat.out.table.desc': 'N items ranked by a metric (top campaigns, top channels…).',
  'help.chat.out.compare': 'Compare',
  'help.chat.out.compare.desc': 'Two sources side-by-side (e.g. GA4 vs Meta) with shared Y axis.',
  'help.chat.out.funnel': 'Funnel',
  'help.chat.out.funnel.desc': 'Multi-step conversion with retention % between each step.',
  'help.chat.out.dashboard': 'Dashboard preview',
  'help.chat.out.dashboard.desc': '4-6 pinnable KPI cards with delta vs previous period.',
  'help.chat.actions.title': 'Actions on each answer',
  'help.chat.actions.excel': 'Excel / CSV',
  'help.chat.actions.excel.desc': 'Real .xlsx download (one sheet per data block).',
  'help.chat.actions.pin': 'Pin',
  'help.chat.actions.pin.desc': 'Saves the widget on your home dashboard (Pro plan).',
  'help.chat.actions.slides': 'Slides',
  'help.chat.actions.slides.desc': '.pptx deck, one slide per insight (Pro plan).',
  'help.chat.actions.rerun': 'Rerun',
  'help.chat.actions.rerun.desc':
    'Re-asks the same question with the opposite mode (Quick ↔ Deep).',
  // Reports
  'help.reports.title': 'Reports',
  'help.reports.body':
    'Generate a monthly / quarterly / custom-range report in HTML, with optional white-label, AI-written analyst note, and split by source. Print to PDF directly from the browser.',
  'help.reports.callout':
    'Auto-send on the 1st of the month is configurable in Settings → Notifications.',
  // Watches
  'help.watches.title': 'Watches (custom alerts)',
  'help.watches.body':
    'Set rules to be notified when a metric crosses a threshold. The Watch Evaluator runs every hour and notifies via in-app or email if the rule fires.',
  'help.watches.op.drops_below': '"Drops below X" — fires when value < threshold.',
  'help.watches.op.rises_above': '"Rises above X" — fires when value > threshold.',
  'help.watches.op.changes_by_pct': '"Changes by ±N%" — fires on big day-over-day variation.',
  'help.watches.op.any_change': '"Any change" — useful for sentinel metrics like connector status.',
  // Pinned
  'help.pinned.title': 'Pinned widgets',
  'help.pinned.body':
    'From any chat KPI or chart, click the 📌 icon to pin it on your home dashboard. Up to 12 pins per workspace. Pro plan only.',
  // Plans
  'help.plans.title': 'Plans & quotas',
  'help.plans.col.feature': 'Feature',
  'help.plans.row.price': 'Monthly price',
  'help.plans.row.connectors': 'Active connectors',
  'help.plans.row.insights': 'Insights / month',
  'help.plans.row.reports': 'Reports generation',
  'help.plans.row.deep': 'Deep chat mode',
  'help.plans.row.pin': 'Pin to dashboard',
  'help.plans.row.slides': 'Slide deck export',
  'help.plans.foot': 'Manage your plan in',
  // Shortcuts
  'help.shortcuts.title': 'Keyboard shortcuts',
  'help.shortcuts.search': 'Open global search / command palette',
  'help.shortcuts.send': 'Send the current chat message',
  'help.shortcuts.close': 'Close any open modal',
  // Contact
  'help.contact.title': "Couldn't find what you needed?",
  'help.contact.body': 'Write to us — we read everything and reply within 24h on business days:',
  // ━━━ /Help page end ━━━
  // ━━━ Activation progress bar (next-steps F) ━━━
  'activation.aria': 'Onboarding progress',
  'activation.label': 'Get started',
  'activation.step.connect': 'Connect a source',
  'activation.step.ask': 'Ask your first question',
  'activation.step.pin': 'Pin your first insight',
  'activation.dismiss': 'Dismiss',
  // ━━━ Common ━━━
  'common.signOut': 'Sign out',
  'common.email': 'Email',
  'common.password': 'Password',
  'common.you': 'You',
  'common.or': 'or',
  'common.loading': 'Loading…',
  'common.back': 'Back',
  'common.backToSite': 'Back to smartanalyst.io',
  'common.delete': 'Delete',
  'common.close': 'Close',

  // ━━━ Nav / layout (App v2 — direction "Le Brief") ━━━
  'nav.workspace': 'Workspace',
  'nav.noWorkspace': 'No workspace',
  'nav.dashboard': 'Dashboard',
  'nav.live': 'Smart tag',
  'nav.menu': 'Menu',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.connectors': 'Connectors',
  'nav.chat': 'Chat',
  'nav.tasks': 'Tasks',
  'nav.home': 'My morning brief',
  'nav.assistant': 'Assistant',
  'nav.veille': 'Watch',
  'nav.todo': 'To do',
  'nav.sources': 'Sources',
  'nav.mySpace': 'My space',
  'nav.yourBusiness': 'Your business',
  'nav.expand': 'Expand panel',
  'nav.collapse': 'Collapse panel',
  'nav.dataUpToDate': 'Up to date',
  'nav.askQuestion': 'Question',

  // ━━━ Brief home (App v2 — accueil narré) ━━━
  'brief.topbar.title': 'My morning brief',
  'brief.greeting': 'Hi {{name}}',
  'brief.score.label': 'health',
  'brief.score.good': 'Going well',
  'brief.score.mid': 'Mixed signals',
  'brief.score.low': 'Needs attention',
  'brief.score.unknown': 'Not enough data yet',
  'brief.score.noData':
    "Connect a data source and I'll start scoring your business in a few minutes.",
  'brief.narrative.up': 'Your score is up {{delta}} points this week — momentum is on your side.',
  'brief.narrative.down':
    'Your score is down {{delta}} points this week — let me show you what changed.',
  'brief.narrative.flat': 'Your score is steady this week.',
  'brief.narrative.one': "There's one thing I'd fix in priority.",
  'brief.narrative.many': "There are {{n}} things I'd look at in priority.",
  'brief.narrative.allGood': 'Nothing critical to flag — keep going.',
  'brief.three.title': 'The 3 things that matter this morning',
  'brief.three.body': "Each item: what's happening, why, and what I'd recommend.",
  'brief.three.empty':
    "Nothing critical right now. I'll surface things here as soon as I spot them.",
  'brief.three.reco': 'My reco',
  'brief.three.cta': 'See all my watch',
  'brief.three.toTasks': 'Add to my tasks',
  'brief.three.added': 'Added',
  'brief.three.askDetails': 'Ask for details',
  'brief.proposed.title': "What I'd suggest you do",
  'brief.proposed.body': "Validate what makes sense; I'll handle the rest.",
  'brief.proposed.empty': 'No proposals waiting. Check back tomorrow morning.',
  'brief.proposed.cta': 'All my tasks',
  'brief.proposed.toCurate': 'to curate',
  'brief.breakdown.title': 'Health by dimension',
  'brief.breakdown.paid': 'Paid',
  'brief.breakdown.organic': 'Organic',
  'brief.breakdown.conversion': 'Conversion',
  'brief.breakdown.revenue': 'Revenue',
  'brief.breakdown.tracking': 'Tracking',
  'brief.kpis.eyebrow': 'At a glance',
  'brief.kpis.empty': 'Connect a source to see your KPIs here.',
  'briefHome.pinned.title': 'Pinned from chat',
  'brief.ask.body': "A question on your numbers? Ask me, I've got everything at hand.",
  'brief.ask.cta': 'Open the assistant',
  'brief.report.cta': 'Generate a report',
  'brief.onboardCta.title': "Let's set you up in 5 minutes.",
  'brief.onboardCta.body':
    "I'll analyze your site, detect your sector and connect your first source.",
  'brief.onboardCta.cta': 'Start onboarding',
  'brief.firstInsight.toast': 'Your first insight just landed!',
  // First-run state (workspace sans canonical_metrics)
  'brief.firstRun.eyebrow': 'WELCOME',
  'brief.firstRun.title': "Hi {{name}} — let's set the stage.",
  'brief.firstRun.body':
    'To brief you each morning on what moved, I need at least one source connected. It takes 30 seconds — pick the tool you check the most.',
  'brief.firstRun.cta': 'Connect a source',
  'brief.firstRun.tour': 'Guided tour',
  'brief.firstRun.tourSub': 'I analyze your site, suggest a profile and walk you through.',
  'brief.firstRun.ask': 'Try the assistant',
  'brief.firstRun.askSub': "Ask a question right away — I'll show you what I can do.",

  // ━━━ Onboarding (App v2 — overlay 5 étapes §3.7C) ━━━
  'onboarding.flowTitle': 'Onboarding',
  'onboarding.step': 'STEP',
  'onboarding.exit': 'Exit',
  'onboarding.exit.confirm':
    'Are you sure you want to exit? Your progress will be lost. You can restart later from your home.',
  'onboarding.error.url': 'Invalid URL. It should start with http(s)://.',
  'onboarding.error.generic': 'Something went wrong. Please try again.',
  'onboarding.s1.hint': '~ 20 seconds',
  'onboarding.s1.title': "What's your website?",
  'onboarding.s1.body':
    "I'll analyze it to understand your business, sector and the tools you use. You'll be able to correct anything next.",
  'onboarding.s1.cta': 'Analyze my site',
  'onboarding.s1.analyzing': 'Analyzing…',
  'onboarding.s2.hint': 'Detected profile',
  'onboarding.s2.title': 'I got your business',
  'onboarding.s2.body': "Here's what I detected from",
  'onboarding.s2.activity': 'ACTIVITY',
  'onboarding.s2.sector': 'SECTOR',
  'onboarding.s2.market': 'MARKET',
  'onboarding.s2.tools': 'TOOLS',
  'onboarding.s2.keywords': 'KEYWORDS',
  'onboarding.s2.correct': 'Correct',
  'onboarding.s2.confirm': "It's right, continue",
  'onboarding.s2.saving': 'Saving…',
  // Step 3 — quiz maturité
  'onboarding.s3.hint': 'Your profile',
  'onboarding.s3.title': 'A few questions so I can help you better',
  'onboarding.s3.body': 'This helps me tailor insights, actions, and priorities to your reality.',
  'onboarding.s3.businessModel': 'Business model',
  'onboarding.s3.businessModel.b2b': 'B2B',
  'onboarding.s3.businessModel.b2c': 'B2C',
  'onboarding.s3.businessModel.b2b2c': 'B2B2C',
  'onboarding.s3.businessModel.marketplace': 'Marketplace',
  'onboarding.s3.businessModel.other': 'Other',
  'onboarding.s3.maturityLevel': 'Your marketing maturity',
  'onboarding.s3.maturityLevel.beginner': 'Getting started',
  'onboarding.s3.maturityLevel.beginner.desc': 'Few tools, mostly manual tracking',
  'onboarding.s3.maturityLevel.intermediate': 'Growing',
  'onboarding.s3.maturityLevel.intermediate.desc': 'GA4, ads running, some automations',
  'onboarding.s3.maturityLevel.advanced': 'Advanced',
  'onboarding.s3.maturityLevel.advanced.desc': 'Multi-channel, attribution, CRM synced',
  'onboarding.s3.primaryGoal': 'What matters most right now?',
  'onboarding.s3.primaryGoal.grow_revenue': 'Grow revenue',
  'onboarding.s3.primaryGoal.reduce_cac': 'Reduce acquisition cost',
  'onboarding.s3.primaryGoal.improve_retention': 'Improve retention',
  'onboarding.s3.primaryGoal.optimize_campaigns': 'Optimize campaigns',
  'onboarding.s3.primaryGoal.understand_customers': 'Understand customers',
  'onboarding.s3.currentStack': 'Tools you use today',
  'onboarding.s3.currentStack.placeholder': 'Select the tools you currently use',
  'onboarding.s3.cta': 'Continue',
  'onboarding.s3.saving': 'Saving…',
  'onboarding.s4.hint': '1-click connection',
  'onboarding.s4.title': 'Connect your first source',
  'onboarding.s4.body': "For your activity, I'd start with",
  'onboarding.s4.desc': 'Sales, products, customers — read-only',
  'onboarding.s4.recommended': 'Recommended',
  'onboarding.s4.connect': 'Connect Shopify',
  'onboarding.s4.later': "I'll connect later",
  'onboarding.s4.fallback':
    "I couldn't read the site automatically — manual mode. Pick the source you check the most and we'll go from there.",
  'onboarding.s5.title': 'Pulling your last 30 days…',
  'onboarding.s5.body': 'Computing your health score and first signals. Usually under a minute.',
  'onboarding.s5.check1': 'Connection ready',
  'onboarding.s5.check2': 'Health score computed',
  'onboarding.s5.check3': 'First signals ready',
  'onboarding.s5.slow': "Taking a bit longer than usual — we're still on it.",
  'onboarding.s5.error': 'Something slowed us down. Opening your space anyway.',
  'onboarding.s5.live': 'Loading your first analysis',
  'onboarding.s6.done': 'Analysis complete',
  'onboarding.s6.title': "Here's what I already spotted.",
  'onboarding.s6.body': 'Your starting health score and the first signals on your real data.',
  'onboarding.s6.cardTitle': 'Good start.',
  'onboarding.s6.cardTitleNamed': 'Good start, {{name}}.',
  'onboarding.s6.cardBody':
    "Your acquisition is solid. Your first lever: conversion — we'll work on it together.",
  'onboarding.s6.cardBodyNoData':
    "I'll have more to say as soon as your data finishes flowing in. In the meantime, here's your fresh space.",
  'onboarding.s6.errorNoWorkspace':
    "Your account isn't fully set up yet. Refresh the page, or contact support if this persists.",
  'onboarding.s6.cta': 'Enter my space',

  // ━━━ Veille (App v2 — surveillance proactive §3.3) ━━━
  'veille.topbar.title': 'My watch',
  'veille.subtitle': 'I monitor continuously · weekly recap on Mondays',
  'veille.title': 'Your watch',
  'veille.intro':
    'I monitor your data 24/7 and warn you BEFORE you have to ask. Each signal comes with its cause and a clear next step.',
  'veille.createAlert': 'Create an alert',
  'veille.filter.all': 'All',
  'veille.filter.critical': 'Alerts',
  'veille.filter.warning': 'To watch',
  'veille.filter.opportunity': 'Opportunities',
  'veille.filter.resolved': 'Handled',
  'veille.sev.critical': 'Alert',
  'veille.sev.high': 'Alert',
  'veille.sev.medium': 'To watch',
  'veille.sev.low': 'Opportunity',
  'veille.cause': 'CAUSE',
  'veille.reco': 'RECO',
  'veille.actions.addToTasks': 'Add to tasks',
  'veille.actions.askAssistant': 'Ask the assistant',
  'veille.actions.ignore': 'Ignore',
  'veille.actions.snooze': 'Snooze',
  'veille.snooze.oneHour': 'For 1 hour',
  'veille.snooze.oneDay': 'Until tomorrow',
  'veille.snooze.oneWeek': 'For 1 week',
  'veille.askPrefix': 'Tell me more',
  'veille.toast.addedToTasks': 'Added to your tasks',
  'veille.toast.ignored': 'Insight ignored',
  'veille.toast.snoozed': 'Snoozed — I will bring it back later',
  'veille.recap.title': 'Weekly recap',
  'veille.recap.allGood': 'Nothing critical this week. Keep going, I keep watching.',
  'veille.recap.criticalCount': '{{n}} alert(s) to handle',
  'veille.recap.warningCount': '{{n}} thing(s) to watch',
  'veille.recap.opportunityCount': '{{n}} opportunity(ies)',
  'veille.empty.allClear':
    "All quiet on the watch front — I'll surface anything new the moment I spot it.",
  'veille.empty.resolved': 'Nothing handled yet.',
  // Watches list (mes veilles : toggle on/off + delete)
  'veille.tab.insights': 'Insights',
  'veille.tab.watches': 'My watches',
  'veille.watchesList.title': 'My watches',
  'veille.watchesList.activeSuffix': 'active',
  'veille.watchesList.empty':
    "You haven't programmed any watch yet. Create one to get pinged when a metric moves.",
  'veille.watchesList.disable': 'Pause this watch',
  'veille.watchesList.enable': 'Resume this watch',
  'veille.watchesList.delete': 'Delete this watch',
  'veille.watchesList.deleteConfirm': 'Delete this watch? This cannot be undone.',
  'veille.watchesList.deleted': 'Watch deleted.',
  'veille.watchesList.deleteError': "Couldn't delete this watch.",
  'veille.watchesList.toggleError': "Couldn't update this watch.",
  'veille.watchesList.triggered': 'fired',
  // First-run state (no canonical_metrics yet)
  'veille.firstRun.eyebrow': 'WATCH',
  'veille.firstRun.title': 'Tell me what to keep an eye on.',
  'veille.firstRun.body':
    'A watch = a condition I monitor for you (ROAS dropping, CPA rising, churn spiking…). I ping you the moment it actually fires. Pick an idea or start your own — but first, connect at least one source.',
  'veille.firstRun.cta': 'Connect a source',
  'veille.firstRun.template.roas': 'Watch a ROAS drop',
  'veille.firstRun.template.roasSub': '"Ping me when my Meta ROAS goes below 2 for 3 days"',
  'veille.firstRun.template.cpa': 'Watch a CPA spike',
  'veille.firstRun.template.cpaSub': '"Tell me when my Google Ads CPA exceeds €30"',
  'veille.firstRun.template.churn': 'Watch churn rising',
  'veille.firstRun.template.churnSub': '"Alert me when my monthly churn goes above 5%"',

  // ━━━ Watch modal (App v2 — création d'alerte custom §3.3) ━━━
  'watchModal.title': 'Create an alert',
  'watchModal.step': 'STEP',
  'watchModal.back': 'Back',
  'watchModal.next': 'Continue',
  'watchModal.create': 'Create the alert',
  'watchModal.creating': 'Creating…',
  'watchModal.toast.created': 'Alert created — I’ll ping you the moment it fires',
  'watchModal.toast.failed': 'Could not create the alert. Try again.',
  'watchModal.s1.label': 'What do you want me to watch?',
  'watchModal.s1.placeholder': 'e.g. Tell me when my ROAS drops below 2',
  'watchModal.s1.hint': "Plain language is fine, I'll match it to the right metric.",
  'watchModal.s1.askToValidate': 'Ask the assistant to confirm.',
  'watchModal.s1.askButton': 'Ask the assistant',
  'watchModal.s1.validating': 'Reading…',
  'watchModal.s1.validated': 'Validated — best match: {{metric}}',
  'watchModal.s1.conf.high': 'Got it — confident match',
  'watchModal.s1.conf.medium': 'Got it — partial match',
  'watchModal.s1.conf.low': "Couldn't match — pick the metric next step",
  'watchModal.s1.error': "Couldn't reach the assistant. You can still continue.",
  'watchModal.s2.metric': 'Metric',
  'watchModal.s2.operator': 'Condition',
  'watchModal.s2.threshold': 'Threshold',
  'watchModal.op.drops_below': 'drops below',
  'watchModal.op.rises_above': 'rises above',
  'watchModal.op.changes_by_pct': 'changes by % (day over day)',
  'watchModal.op.any_change': 'any change',
  'watchModal.s3.channels': 'How should I notify you?',
  'watchModal.s3.inApp': 'In-app (bell)',
  'watchModal.s3.email': 'Email',
  'watchModal.s3.summary': 'Summary',
  'watchModal.s3.if': 'If',
  'watchModal.summary.dropsBelow': 'drops below',
  'watchModal.summary.risesAbove': 'rises above',
  'watchModal.summary.changesByPct': 'changes by {{n}}% day over day',
  'watchModal.summary.anyChange': 'changes (any direction)',

  // ━━━ Reports (App v2 — synthèse mensuelle §3.6) ━━━
  'reports.topbar.title': 'My reports',
  'reports.subtitle': 'Monthly summary · for you or to share',
  'reports.generate': 'Generate a report',
  'reports.empty': 'No report yet. Generate one for the current month.',
  'reports.selectOne': 'Select a report to preview it here.',
  // First-run state (no report yet)
  'reports.firstRun.eyebrow': 'REPORTS',
  'reports.firstRun.title': 'Generate your first report.',
  'reports.firstRun.body':
    'A report = a clean, shareable summary of your month: health score, KPIs, what mattered, what to do. Pick a period, I do the rest in 30 seconds.',
  'reports.firstRun.cta': 'Generate now',
  'reports.printPdf': 'Print as PDF',
  'reports.printBlocked': 'Pop-up blocked. Allow pop-ups to print.',
  'reports.notReady': 'Report not ready yet.',
  'reports.failed': 'Generation failed. Try again.',
  'reports.deleteConfirm': 'Delete this report? This cannot be undone.',
  'reports.toast.generated': 'Report generated',
  'reports.toast.failed': 'Could not generate the report.',
  'reports.toast.deleted': 'Report deleted',
  'reports.status.draft': 'Draft',
  'reports.status.generating': 'Generating',
  'reports.status.ready': 'Ready',
  'reports.status.sent': 'Sent',
  'reports.status.failed': 'Failed',
  'reports.kind.monthly': 'Monthly',
  'reports.kind.quarterly': 'Quarterly',
  'reports.kind.custom': 'Custom range',
  'reports.gen.title': 'Generate a report',
  'reports.gen.kind': 'Type',
  'reports.gen.start': 'Start',
  'reports.gen.end': 'End',
  'reports.gen.whiteLabel': 'White-label (no SmartAnalyst branding)',
  'reports.gen.advanced': 'Advanced — sources, segmentation, comparison',
  'reports.gen.sources': 'Sources to include',
  'reports.gen.sourcesNone': 'No active connector yet.',
  'reports.gen.sourcesHint': 'Leave blank to include all connected sources.',
  'reports.gen.segmentBy': 'Segment KPIs by',
  'reports.gen.segmentNone': 'No segmentation (totals)',
  'reports.gen.segmentSource': 'By source (mini-table per KPI)',
  'reports.gen.compare': 'Compare to previous period (same length)',
  'reports.gen.cta': 'Generate',
  'reports.gen.generating': 'Generating…',

  // ━━━ Sources (App v2 — connecteurs + librairie §3.7) ━━━
  'sources.topbar.title': 'My data sources',
  'sources.subtitle': 'Live connectors and file library',
  'sources.title': 'Your data sources',
  'sources.intro':
    "Connectors feed your dashboard in real time. The library is your assistant's raw material — anything you drop in becomes queryable.",
  'sources.tab.connectors': 'Live connectors',
  'sources.tab.library': 'File library',
  'sources.counters.active': '{{n}} active',
  'sources.counters.reconnect': '{{n}} to reconnect',
  'sources.addSource': 'Add a source',
  'sources.recommended': 'Recommended for your activity',
  'sources.reconnect': 'Reconnect',
  'sources.neverSynced': 'never synced',
  'sources.status.synced': 'Synced',
  'sources.status.expired': 'Expired',
  'sources.health.healthy': 'Healthy',
  'sources.health.stale': 'Stale',
  'sources.health.expired': 'Reconnect needed',
  'sources.health.failing': 'Sync failing',
  'sources.health.unknown': 'Pending',
  // ─── Activation error states (cahier §3 Lot 0 / §4.1 / §4.7) ────────
  'activation.error.oauth_error.title': "Couldn't finish connecting {{source}}",
  'activation.error.oauth_error.body':
    'The provider returned an error during the OAuth handshake. Nothing was saved — you can try again.',
  'activation.error.oauth_provider_denied.title': 'Access wasn’t granted',
  'activation.error.oauth_provider_denied.body':
    'You — or someone in your account — declined the permission request. We need it to read your data; pick another account or grant access to retry.',
  'activation.error.scope_mismatch.title': 'Partial permissions granted on {{source}}',
  'activation.error.scope_mismatch.body':
    'Some metrics will stay empty until full access is given. Reconnect to fix it — or keep going with what we have.',
  'activation.error.insufficient_data.title': 'Not enough data yet',
  'activation.error.insufficient_data.body':
    'Your account is connected, but there’s less than 7 days of data to learn from. We’ll start producing insights as soon as we have enough.',
  'activation.error.workspace_not_ready.title': 'Your workspace isn’t ready yet',
  'activation.error.workspace_not_ready.body':
    'We’re finishing your account setup. Refresh in a minute — if it persists, contact support.',
  'activation.error.unknown.title': 'Something didn’t go through',
  'activation.error.unknown.body':
    'We weren’t able to confirm what happened. You can try again or come back later — nothing is broken on your side.',
  'activation.cta.retry': 'Try again',
  'activation.cta.reconnect': 'Reconnect',
  'activation.cta.dismiss': 'Dismiss',
  'activation.cta.contactSupport': 'Contact support',
  'sources.empty.connectors':
    'No source connected yet. Plug GA4, Meta Ads or Stripe in a few clicks — your dashboard will fill up automatically.',
  'sources.dropzone.title': 'Drop a file here',
  'sources.dropzone.body': 'CSV, Excel, PDF or image — reusable in the assistant',
  'sources.dropzone.browse': 'Browse my files',
  'sources.dropzone.uploading': 'Uploading…',
  'sources.empty.files': 'No file yet. Drop a CSV, Excel, PDF or image to query it.',
  'sources.askAbout': 'Ask',
  'sources.toast.uploaded': 'File uploaded',
  'sources.toast.uploadFailed': 'Upload failed',
  'sources.toast.deleted': 'File deleted',

  // ━━━ Chat ━━━
  'chat.kicker': 'Ask SmartAnalyst',
  'chat.title': 'What do you want to know?',
  'chat.placeholder': 'e.g. Why did Meta CVR drop yesterday?',
  'chat.send': 'Send',
  'chat.thinking': 'Thinking…',
  'chat.stop': 'Stop',
  'chat.stop.aria': 'Stop generation',
  'chat.copy': 'Copy',
  'chat.mode.aria': 'Response depth',
  'chat.mode.fast': 'Quick',
  'chat.mode.deep': 'Deep',
  'chat.feedback.up': 'Helpful answer',
  'chat.feedback.down': 'Mark as inaccurate',
  // ━━━ Global search (cahier §3 Lot 2) ━━━
  'search.aria': 'Search across conversations, insights and reports',
  'search.placeholder': 'Search…',
  'search.hint': 'Type at least 2 characters',
  'search.loading': 'Searching…',
  'search.loadError': 'Search failed.',
  'search.noResults': 'No results',
  'search.untitled': 'Untitled',
  'search.section.conversations': 'Conversations',
  'search.section.insights': 'Insights',
  'search.section.reports': 'Reports',
  'search.open.aria': 'Open search (Cmd+K)',
  'chat.emptyState':
    'Ask anything about your marketing performance. The first connectors and metric integration are coming — for now SmartAnalyst answers from general marketing knowledge.',
  'chat.suggestions': 'Try',
  'chat.suggestion1': 'How do you compute a blended CAC?',
  'chat.suggestion2': 'What is a healthy LTV/CAC ratio for a SaaS?',
  'chat.suggestion3': 'How do I detect Meta Ads creative fatigue?',
  'chat.error': 'Something went wrong. Try again.',
  'chat.error.budget':
    'Your monthly AI budget is reached. Raise it in Settings or wait until the 1st.',
  'chat.error.budget.cta': 'Upgrade →',
  'chat.error.rateLimit':
    'Too many questions at once — the AI is saturated. Give it a few seconds and try again.',
  'chat.error.timeout': 'That took too long. Try again — sometimes the second pass is faster.',
  'chat.error.providerDown': 'The AI is temporarily unavailable. Try again in a minute.',
  'chat.error.creditDepleted':
    'Deep mode is temporarily unavailable. Switch back to Quick mode to keep going.',
  'chat.error.retry': 'Retry',
  'chat.modeDowngraded':
    'Deep mode is not available on your plan — this response used Fast mode instead.',
  'chat.you': 'You',
  'chat.assistant': 'SmartAnalyst',
  'chat.sources': 'Sources',
  'chat.attachedFile': 'attached file',
  'chat.removeAttachment': 'Remove attachment',
  'chat.new.cta': 'New conversation',
  'chat.new.title': 'Start a fresh conversation — current one stays saved.',
  'chat.history.cta': 'My conversations',
  'chat.history.loading': 'Loading…',
  'chat.history.empty': 'No saved conversations yet.',
  // ━━━ Chat V2.1 — empty state hero + quick cards (cahier 22b §3.1) ━━━
  'chat.hero.title': 'What can I help you with today?',
  'chat.hero.placeholder.1': 'Compare my channels over 30 days…',
  'chat.hero.placeholder.2': 'Why did my ROAS drop this week?',
  'chat.hero.placeholder.3': 'Generate my monthly report',
  'chat.hero.placeholder.4': 'What 3 actions should I prioritise?',
  'chat.hero.placeholder.5': 'Which campaigns make 80% of revenue?',
  // First-run state (no connectors yet)
  'chat.empty.firstRun.eyebrow': 'Get started',
  'chat.empty.firstRun.title': 'Connect your first source',
  'chat.empty.firstRun.body':
    'I need a source connected (GA4, Meta Ads, Google Ads, Stripe…) before I can help. It takes 2 minutes — then come back here and ask anything.',
  'chat.empty.firstRun.cta': 'Connect a source',
  'chat.empty.firstRun.hint':
    'No source = no data. Once a connector is active, the quick-start cards appear here.',
  'chat.qc.compare.title': 'Compare channels',
  'chat.qc.compare.desc': '30-day breakdown',
  'chat.qc.compare.prompt': 'Compare my acquisition channels over the last 30 days.',
  'chat.qc.revenue.title': 'Revenue trend',
  'chat.qc.revenue.desc': 'Daily curve this week',
  'chat.qc.revenue.prompt': 'Plot my revenue per day over this week.',
  'chat.qc.report.title': 'Monthly report',
  'chat.qc.report.desc': 'Executive template',
  'chat.qc.report.prompt': 'Generate my report for the current month, executive template.',
  'chat.qc.funnel.title': 'E-commerce funnel',
  'chat.qc.funnel.desc': 'Sessions → orders',
  'chat.qc.funnel.prompt': 'Show me the conversion funnel: sessions → add-to-cart → orders.',
  'chat.qc.pareto.title': 'Pareto budget',
  'chat.qc.pareto.desc': '80/20 on ROAS',
  'chat.qc.pareto.prompt': 'Which 20% of my campaigns drive 80% of ROAS?',
  'chat.qc.alerts.title': 'Auto alerts',
  'chat.qc.alerts.desc': 'Anomalies this week',
  'chat.qc.alerts.prompt': 'Which metrics dropped abnormally this week?',
  'chat.qc.compare_prev.title': 'Vs. last period',
  'chat.qc.compare_prev.desc': 'Deltas main KPIs',
  'chat.qc.compare_prev.prompt': 'Compare this month to the previous one — main KPIs with deltas.',
  'chat.qc.actions.title': 'What to do',
  'chat.qc.actions.desc': '3 prioritised actions',
  'chat.qc.actions.prompt': 'Give me 3 concrete actions to prioritise based on my data.',
  // ━━━ Chat V2.1 — composer toolbar (cahier 22b §3.2) ━━━
  'chat.toolbar.attach': 'Attach a file',
  'chat.toolbar.sources': 'Filter sources',
  'chat.toolbar.sources.allActive': 'All active sources',
  'chat.toolbar.sources.empty': 'No active source — connect one first.',
  'chat.toolbar.sources.clear': 'Reset to all sources',
  'chat.toolbar.send.aria': 'Send message',
  // ━━━ Chat V2.1 — tool badge during streaming (cahier 22b §3.5) ━━━
  'chat.tool.running': 'Querying',
  'chat.tool.running.ga4': 'Reading Google Analytics…',
  'chat.tool.running.meta_ads': 'Reading Meta Ads…',
  'chat.tool.running.google_ads': 'Reading Google Ads…',
  'chat.tool.running.stripe': 'Reading Stripe…',
  'chat.tool.running.search_console': 'Reading Search Console…',
  'chat.tool.running.get_health_score': 'Computing health score…',
  'chat.tool.running.list_top_insights': 'Listing top insights…',
  'chat.tool.running.list_pending_actions': 'Listing pending actions…',
  'chat.tool.running.get_metric_series': 'Pulling metric series…',
  'chat.tool.running.get_traffic_sources': 'Querying GA4 live…',
  'chat.tool.running.create_action_card': 'Creating action card…',
  'chat.tool.running.create_watch': 'Creating watch…',
  'chat.tool.running.compute_table_from_metrics': 'Building comparison table…',
  'chat.tool.running.compare_metrics': 'Comparing the two sources…',
  'chat.tool.running.compute_funnel': 'Computing funnel…',
  'chat.tool.running.build_dashboard_preview': 'Building dashboard preview…',
  'chat.tool.running.query_ga4': 'Querying Google Analytics live…',
  'chat.tool.running.query_meta_ads': 'Querying Meta Ads live…',
  'chat.tool.running.query_stripe': 'Querying Stripe live…',
  'chat.tool.running.query_shopify': 'Querying Shopify live…',
  'chat.tool.running.query_search_console': 'Querying Search Console live…',
  'chat.tool.running.analyze': 'Running analyst verdict…',
  'chat.plan.title': 'Analysis plan',
  'chat.actionPlan.badge': 'Action plan',
  'chat.actionPlan.add': 'Add to my tasks',
  'chat.actionPlan.added': 'Added to your tasks',
  'chat.actionPlan.viewTasks': 'View my tasks',
  'chat.actionPlan.failed': 'Could not add the tasks. Try again.',
  'chat.actionPlan.impact': 'Impact',
  'chat.actionPlan.effort': 'Effort',
  'chat.actionPlan.prio.critical': 'Critical',
  'chat.actionPlan.prio.high': 'High',
  'chat.actionPlan.prio.medium': 'Medium',
  'chat.actionPlan.prio.low': 'Low',
  'chat.history.rename': 'Rename',
  'chat.history.renamePrompt': 'New conversation title:',
  'chat.history.delete': 'Delete',
  'chat.history.deleteConfirm': 'Delete this conversation? This cannot be undone.',
  // ━━━ Chat V2.1 — action shelf (cahier 22b §3.4) ━━━
  'chat.shelf.excel': 'Excel',
  'chat.shelf.excel.aria': 'Export answer as Excel',
  'chat.shelf.csv': 'CSV',
  'chat.shelf.csv.aria': 'Export answer as CSV',
  'chat.shelf.copy': 'Copy',
  'chat.shelf.copy.aria': 'Copy answer as markdown',
  'chat.shelf.copy.done': 'Copied',
  'chat.shelf.rerun': 'Rerun',
  'chat.shelf.rerun.aria': 'Rerun the same question with the opposite mode',
  'chat.shelf.pin': 'Pin',
  'chat.shelf.pin.aria': 'Pin a chart from this answer to your dashboard',
  'chat.shelf.pin.soon': 'Coming with V2.3',
  'chat.shelf.report': 'Report',
  'chat.shelf.report.aria': 'Open the report generator pre-filled with this answer',
  'chat.shelf.slides': 'Slides',
  'chat.shelf.slides.aria': 'Generate a slide deck (.pptx) of this answer',
  'chat.shelf.proOnly': 'Pro plan required',
  // ━━━ Pin to dashboard (Lot V2.3) ━━━
  'chat.pin.success': 'Pinned to dashboard.',
  'chat.pin.error': 'Could not pin this widget.',
  'chat.pin.max': 'Max pinned widgets reached — remove one first.',
  'chatWidget.open': 'Open AI assistant',
  'chatWidget.close': 'Close',
  'chatWidget.title': 'Ask SmartAnalyst',
  'chatWidget.greeting': 'Ask me anything about your marketing, your data, or your audit.',
  'nav.reports': 'Reports',
  'nav.files': 'Files',
  'nav.settings': 'Settings',
  'nav.help': 'Help',
  'nav.soon': 'soon',

  // ━━━ Locale switcher ━━━
  'locale.label': 'Language',
  'locale.english': 'English',
  'locale.french': 'Français',

  // ━━━ Login ━━━
  'login.welcomeBack': 'Welcome back.',
  'login.subtitle': 'Sign in to your SmartAnalyst workspace.',
  'login.emailPlaceholder': 'you@company.com',
  'login.passwordPlaceholder': '••••••••••••',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.somethingWentWrong': 'Something went wrong',
  'login.newToSmartAnalyst': 'New to SmartAnalyst?',
  'login.createAccount': 'Create an account',
  'login.googleButton': 'Continue with Google',
  'login.googleRedirecting': 'Redirecting…',
  'login.googleStartError': 'Could not start Google sign-in',
  'login.forgotPassword': 'Forgot password?',

  // ━━━ Password reset ━━━
  'reset.requestTitle': 'Reset your password.',
  'reset.requestSubtitle':
    "Enter the email tied to your SmartAnalyst account. We'll send a reset link.",
  'reset.requestSubmit': 'Send the reset link',
  'reset.requestSubmitting': 'Sending…',
  'reset.requestDoneTitle': 'Check your inbox.',
  'reset.requestDoneBody':
    'If an account exists for {{email}}, a reset link is on its way. It expires in 1 hour.',
  'reset.confirmTitle': 'Choose a new password.',
  'reset.confirmSubtitle': "Pick something memorable. You'll be signed in right after.",
  'reset.newPassword': 'New password',
  'reset.confirmPassword': 'Confirm new password',
  'reset.confirmSubmit': 'Update password',
  'reset.confirmSubmitting': 'Updating…',
  'reset.backToLogin': 'Back to sign-in',
  'reset.mismatch': "Passwords don't match.",
  'reset.tooShort': 'Password must be at least 12 characters.',
  'reset.linkExpired': 'This reset link is invalid or expired. Request a new one.',
  'reset.error': 'Something went wrong. Try again.',

  // ━━━ Signup ━━━
  'signup.title': 'Create your workspace.',
  'signup.subtitle': 'Get started in under a minute.',
  'signup.organizationName': 'Organization name',
  'signup.organizationPlaceholder': 'Acme Inc.',
  'signup.workEmail': 'Work email',
  'signup.passwordPlaceholder': 'At least 12 characters',
  'signup.passwordHint': '12+ characters. Use a passphrase you’ll remember.',
  'signup.submit': 'Create account',
  'signup.submitting': 'Creating account…',
  'signup.alreadyHaveAccount': 'Already have an account?',
  'signup.signIn': 'Sign in',
  'signup.googleButton': 'Sign up with Google',

  // ━━━ Auth callback ━━━
  'callback.signingIn': 'Signing you in…',
  'callback.oneSec': 'One sec.',
  'callback.failedTitle': 'Google sign-in failed',
  'callback.backToLogin': 'Back to sign-in',
  'callback.err.accessDenied': 'You denied access to Google.',
  'callback.err.notConfigured': 'Google sign-in isn’t enabled on the server yet.',
  'callback.err.codeExchange': 'Google rejected the authorization code. Try again.',
  'callback.err.supabase':
    'Supabase rejected the Google session. Check that the provider is enabled.',
  'callback.err.stateInvalid': 'The link expired. Try again from the sign-in page.',
  'callback.err.stateMissing': 'Invalid link. Try again.',
  'callback.err.missingCodeOrState': 'Invalid Google response. Try again.',
  'callback.err.missingResponse': 'Missing response.',
  'callback.err.incompleteResponse': 'Incomplete response. Try again.',
  'callback.err.malformed': 'Malformed response.',
  'callback.err.generic': 'Google error: {{code}}',

  // ━━━ Beta lockdown ━━━
  'betaLocked.badge': 'Private beta',
  'betaLocked.title': 'SmartAnalyst is in private beta.',
  'betaLocked.body':
    "We're finishing the app for a handful of carefully picked marketers. Join the waitlist and we'll reach out with access as soon as we're ready.",
  'betaLocked.cta': 'Join the waitlist',
  'betaLocked.alreadyInvited': 'Already invited?',
  'betaLocked.backToLogin': 'Back to sign-in',

  // ━━━ Dashboard ━━━
  'dashboard.overview': 'Overview',
  'dashboard.greetingNamed': 'Hey {{name}}, here’s what changed.',
  'dashboard.greeting': 'Hey there, here’s what changed.',
  'dashboard.window.7d': '7d',
  'dashboard.window.30d': '30d',
  'dashboard.window.90d': '90d',
  'dashboard.emptyConnectorsTitle': 'Connect a data source to start.',
  'dashboard.emptyConnectorsBody':
    'Plug GA4, Meta Ads or Stripe in 2 clicks. We’ll begin syncing the last 90 days so your dashboard fills up automatically.',
  'dashboard.addConnector': 'Add a connector →',
  'dashboard.couldNotLoad': 'Could not load metrics. {{message}}',
  'dashboard.tile.syncing': 'Syncing…',
  'dashboard.tile.noData': 'No data yet',
  'dashboard.tile.noBaseline': 'vs. previous: no baseline',
  'dashboard.tile.vsPrevious': 'vs. previous period',
  'dashboard.next.01.title': 'Connect more sources',
  'dashboard.next.01.body':
    'The more data you connect, the sharper the AI’s cross-source insights become.',
  'dashboard.next.01.cta': 'Open connectors',
  'dashboard.next.02.title': 'Ask your first question',
  'dashboard.next.02.body': 'Try “What changed this week?” or “Why did CVR drop yesterday?”.',
  'dashboard.next.02.cta': 'Open chat',
  'dashboard.next.03.title': 'Schedule a weekly report',
  'dashboard.next.03.body': 'Auto-generated PDF, sent every Monday with an exec summary.',
  'dashboard.next.03.cta': 'Configure',
  'dashboard.tile.sessions': 'Sessions',
  'dashboard.tile.conversions': 'Conversions',
  'dashboard.tile.revenue': 'Revenue',
  'dashboard.tile.adSpend': 'Ad spend',
  'dashboard.tile.mrr': 'MRR',
  'dashboard.tile.activeCustomers': 'Active customers',
  'dashboard.tile.newCustomers': 'New customers',
  'dashboard.tile.failedPayments': 'Failed payments',
  'dashboard.tile.activeUsers': 'Active users',
  'dashboard.tile.bounceRate': 'Bounce rate',
  'dashboard.tile.metaSpend': 'Meta spend',
  'dashboard.tile.metaClicks': 'Meta clicks',
  'dashboard.tile.metaConversions': 'Meta conversions',
  'dashboard.tile.metaRoas': 'Meta ROAS',
  'dashboard.tile.googleAdsSpend': 'Google Ads spend',
  'dashboard.tile.googleAdsClicks': 'Google Ads clicks',
  'dashboard.tile.googleAdsConversions': 'Google Ads conv.',
  'dashboard.tile.googleAdsCtr': 'Google Ads CTR',
  'dashboard.tile.shopifyRevenue': 'Shopify revenue',
  'dashboard.tile.orders': 'Orders',
  'dashboard.tile.aov': 'AOV',
  'dashboard.tile.organicClicks': 'Organic clicks',
  'dashboard.tile.organicImpressions': 'Organic impressions',
  'dashboard.tile.organicCtr': 'Organic CTR',
  'dashboard.tile.avgPosition': 'Avg position',

  // ━━━ Home: Tracking Health ━━━
  'home.health.kicker': 'Tracking health',
  'home.health.tagActive': 'SmartTag is live',
  'home.health.tagInactive': 'SmartTag not installed',
  'home.health.activeBody': 'Last event {{ago}} ago · {{events7}} events in the past 7 days.',
  'home.health.inactiveBody':
    'Install the SmartTag to compare your own ground truth with what GA4 / Meta / Stripe report.',
  'home.health.installCta': 'Install SmartTag',
  'home.health.events7d': 'Events 7d',
  'home.health.events30d': 'Events 30d',
  'home.health.sources': 'Connected sources',
  'home.health.confidence.high': 'High confidence',
  'home.health.confidence.medium': 'Medium confidence',
  'home.health.confidence.low': 'Low confidence',

  // ━━━ Home: Brief du jour (Insights + Actions) ━━━
  'home.brief.kicker': "Today's brief",
  'home.brief.title': 'What needs your attention.',
  'home.brief.count': '{{count}} open',
  'home.brief.empty.body':
    'No insights yet. Connect your data sources and SmartAnalyst will start surfacing what matters.',
  'home.brief.empty.cta': 'Open connectors',
  'home.brief.evidence': 'More evidence',
  'home.brief.actions': 'Recommended actions',
  'home.brief.limitations': 'Limitations',
  'home.brief.markResolved': 'Mark resolved',
  'home.brief.snooze': 'Snooze',
  'home.brief.dismiss': 'Dismiss',
  'home.brief.impact': 'Impact',
  'home.brief.effort': 'Effort',
  'home.brief.priority': 'Priority',
  'home.brief.task.validate': 'Validate',
  'home.brief.task.archive': 'Archive',

  // ━━━ Tasks page ━━━
  'tasks.kicker': 'Tasks',
  'tasks.title': 'What needs to be done.',
  'tasks.subtitle': 'Curate what the AI suggests, work on what you committed to, archive the rest.',
  'tasks.loadError': 'Could not load tasks.',
  'tasks.col.inbox': 'Inbox (to curate)',
  // ━━━ NotificationCenter (cahier §3 Lot 1) ━━━
  'notif.title': 'Notifications',
  'notif.markAll': 'Mark all as read',
  'notif.loading': 'Loading…',
  'notif.loadError': 'Could not load notifications.',
  'notif.empty.title': 'Nothing new',
  'notif.empty.body': "We'll ping you when something matters.",
  'notif.aria.bell': 'Notifications',
  'notif.aria.unread': '{{count}} unread notifications',
  'notif.aria.panel': 'Notifications panel',
  // ━━━ Design-system d'états (cahier §3 Lot 2 + §4.9) ━━━
  'state.error.title': 'Something went wrong',
  'state.error.body': "We couldn't load this. Try again — if it persists, ping support.",
  'state.error.retry': 'Try again',
  'state.permission.title': 'Not available on your plan',
  'state.permission.body': 'Upgrade to unlock this feature, or ask your workspace admin.',
  'state.permission.upgrade': 'See plans',
  // ━━━ DataFreshness (cahier §3 Lot 2 + §4.2) ━━━
  'freshness.loading': 'Checking sources…',
  'freshness.none': 'No source connected',
  'freshness.upToDate': 'Up to date',
  'freshness.lastSync': 'Last sync {{rel}}',
  'freshness.needsHelp': 'A source needs help',
  'tasks.col.today': 'Today',
  'tasks.col.done': 'Done',
  'tasks.empty.inbox': 'No new suggestions.',
  'tasks.empty.today': 'Nothing on your list.',
  'tasks.empty.done': 'Nothing done yet.',
  'tasks.emptyAll.body': 'No tasks yet. The AI will surface them as it analyses your data.',
  'tasks.emptyAll.cta': 'Connect a source',
  'tasks.action.validate': 'Validate',
  'tasks.action.archive': 'Archive',
  'tasks.action.markDone': 'Mark done',
  'tasks.action.reopen': 'Reopen',
  'tasks.action.emailBrief': 'Email brief',
  'tasks.emailModal.title': 'Send as brief',
  'tasks.emailModal.recipient': 'Recipient email',
  'tasks.emailModal.note': 'Personal note (optional)',
  'tasks.emailModal.notePlaceholder': 'Anything you want to add for the recipient.',
  'tasks.emailModal.cancel': 'Cancel',
  'tasks.emailModal.send': 'Send',
  'tasks.emailModal.sending': 'Sending…',
  'tasks.emailModal.sent': 'Sent ✓',

  // ━━━ Onboarding checklist ━━━
  'onboarding.kicker': 'Getting started',
  'onboarding.title': 'Set up SmartAnalyst in 3 steps.',
  'onboarding.dismiss': 'Dismiss',
  'onboarding.step.connect.title': 'Connect a data source',
  'onboarding.step.connect.body': 'GA4, Meta Ads, Stripe… in 2 clicks. We sync the last 30 days.',
  'onboarding.step.connect.cta': 'Connect',
  'onboarding.step.tag.title': 'Install the SmartTag (optional)',
  'onboarding.step.tag.body': 'Your own ground truth, to cross-check what GA4 / Meta report.',
  'onboarding.step.tag.cta': 'Install',
  'onboarding.step.ask.title': 'Ask your first question',
  'onboarding.step.ask.body': 'Try “What changed this week?” — get causes, evidence and actions.',
  'onboarding.step.ask.cta': 'Open chat',

  // ━━━ Connectors ━━━
  'connectors.kicker': 'Integrations',
  'connectors.title': 'Connect your data sources.',
  'connectors.subtitle': '{{available}} live · {{soon}} on the roadmap. Don’t see yours?',
  'connectors.tellUs': 'Tell us',
  'connectors.searchPlaceholder': 'Search integrations…',
  'connectors.category.all': 'All',
  'connectors.category.Analytics': 'Analytics',
  'connectors.category.Advertising': 'Advertising',
  'connectors.category.Payments': 'Payments',
  'connectors.category.CRM': 'CRM',
  'connectors.category.Email & SMS': 'Email & SMS',
  'connectors.category.Ecommerce': 'Ecommerce',
  'connectors.category.Product & Events': 'Product & Events',
  'connectors.category.SEO': 'SEO',
  'connectors.category.Social': 'Social',
  'connectors.category.Support': 'Support',
  'connectors.category.Data warehouse': 'Data warehouse',
  'connectors.category.Spreadsheet & Files': 'Spreadsheet & Files',
  'connectors.section.available': 'Available',
  'connectors.section.soon': 'Coming soon',
  'connectors.emptyResults': 'No integration matches your search.',
  'connectors.badge.connected': 'Connected',
  'connectors.badge.soon': 'Soon',
  'connectors.auth.oauth': 'OAuth',
  'connectors.auth.apiKey': 'API key',
  'connectors.action.connect': 'Connect',
  'connectors.action.opening': 'Opening…',
  'connectors.action.cancel': 'Cancel',
  'connectors.action.disconnect': 'Disconnect',
  'connectors.action.disconnecting': 'Disconnecting…',
  'connectors.action.notifyMe': 'Notify me',
  'connectors.action.sync': 'Sync now',
  'connectors.action.reconnect': 'Reconnect',
  'sources.file.deleteConfirm': 'Delete this file? This cannot be undone.',
  'connectors.action.syncing': 'Syncing…',
  'connectors.confirmDisconnect': 'Disconnect {{name}}?',
  'connectors.err.startOauth': 'Could not start the OAuth flow',
  'connectors.err.disconnect': 'Could not disconnect',
  'connectors.err.sync': 'Could not sync',
  'connectors.sync.success': 'Synced {{count}} metrics',
  'connectors.apikey.label': 'Paste your {{name}} API key',
  'connectors.apikey.save': 'Connect',
  'connectors.apikey.saving': 'Connecting…',
  'connectors.loading': 'Loading the catalog…',
  'connectors.badge.notConfigured': 'OAuth app not configured yet on our side.',
  'connectors.subdomain.label': 'Your Shopify store',

  // ━━━ Live (SmartTag real-time dashboard) ━━━
  'live.kicker': 'Smart tag',
  'live.title': "What's happening on your site, right now.",
  'live.subtitle':
    'Events streamed live from your SmartAnalyst tag. No storage in the free tier — refresh resets the view.',
  'live.stat.activeSessions': 'Active sessions',
  'live.stat.pageviews': 'Pageviews',
  'live.stat.clicks': 'Clicks',
  'live.stat.errors': 'Errors',
  'live.recentEvents': 'Recent events',
  'live.windowHint': 'Last 5 minutes',
  'live.empty': 'No events yet — install the tag on your site to start streaming.',
  'live.connecting': 'Connecting to the stream…',
  'live.conn.connecting': 'Connecting',
  'live.conn.connected': 'Live',
  'live.conn.disconnected': 'Disconnected',
  'live.conn.error': 'Connection error',
  'live.conn.unauthorized': 'Unauthorized',
  'live.err.unauthorized': "Your session does not have access to this workspace's live stream.",
  'live.eventType.pageview': 'view',
  'live.eventType.click': 'click',
  'live.eventType.error': 'error',
  'live.eventType.session_start': 'session',
  'live.eventType.custom': 'event',
  'live.install.title': 'Install your SmartTag',
  'live.install.subtitle':
    "Paste this snippet in your site's <head> to start receiving events here.",
  'live.install.cta.guide': 'Full install guide',
  'live.install.cta.scroll': 'See events ↓',
  'live.install.cta.audit': 'Audit my site',

  // ━━━ Smart tag onboarding (no events received yet) ━━━
  'live.onboarding.kicker': 'Smart tag setup',
  'live.onboarding.title': 'Active your Smart tag in 3 minutes',
  'live.onboarding.subtitle':
    'One snippet to paste in your site. Real-time analytics, on-demand SEO/GEO audits, zero cookies.',
  'live.onboarding.why.title': 'What the Smart tag gives you',
  'live.onboarding.why.analytics.title': 'Real-time analytics',
  'live.onboarding.why.analytics.body':
    'Pageviews, clicks, errors streaming live — see your traffic as it happens, not the day after.',
  'live.onboarding.why.audit.title': 'On-demand SEO + GEO audit',
  'live.onboarding.why.audit.body':
    'Run a 4-axis audit (SEO, AI search, perf, citation-worthiness) from any page in 30 seconds.',
  'live.onboarding.why.privacy.title': 'No cookie, GDPR-friendly',
  'live.onboarding.why.privacy.body':
    'No cookie banner required. Session ID lives in sessionStorage — cleared at tab close. CNIL-compatible.',
  'live.onboarding.step1.title': 'Copy your snippet',
  'live.onboarding.step1.body': "It's pre-filled with your workspace write key. Just copy it.",
  'live.onboarding.step2.title': 'Paste it in your <head>',
  'live.onboarding.step2.body':
    'On every page you want to track (or in your shared layout / template). WordPress, Shopify, Webflow, Ghost — see the full guide for step-by-step.',
  'live.onboarding.step2.cta': 'CMS-specific install guide',
  'live.onboarding.step3.title': "Verify it's working",
  'live.onboarding.step3.body':
    'Once installed, open your site in another tab and click around. Then come back here and hit "Test install" — we\'ll detect the first event live.',
  'live.onboarding.step3.button': 'Test install',
  'live.onboarding.step3.checking': 'Listening for events… (up to 30 seconds)',
  'live.onboarding.step3.success': 'Tag detected — redirecting to your dashboard',
  'live.onboarding.step3.timeout':
    'No event received in 30 seconds. Check that the snippet is in your <head> and that you opened your site in another tab.',
  'live.onboarding.step3.retry': 'Retry',

  // ━━━ Settings ━━━
  'settings.kicker': 'Settings',
  'settings.title': 'Account & workspace.',
  'settings.section.profile': 'Profile',
  'settings.section.workspace': 'Workspace',
  'settings.section.security': 'Security',
  'settings.section.session': 'Session',
  'settings.section.language': 'Language',
  'settings.section.notifications': 'Notifications',
  'settings.section.aiUsage': 'AI usage',
  'settings.section.data': 'Your data',
  // ━━━ Quota chip + toast (next-steps A — quota IA visible) ━━━
  'quota.chip.connectors': 'sources',
  'quota.chip.insights': 'insights',
  'quota.chip.normalHint': 'Your monthly quota — click to manage your plan',
  'quota.chip.criticalHint': 'You are about to hit your monthly limit',
  'quota.chip.exceeded': 'Monthly limit reached',
  'quota.toast.insights.warn.title': 'You are nearing your insights quota',
  'quota.toast.insights.exceeded.title': 'Insights monthly limit reached',
  'quota.toast.connectors.warn.title': 'Almost at your connectors limit',
  'quota.toast.connectors.exceeded.title': 'Connectors limit reached',
  'quota.chip.aiTokens': 'AI tokens',
  'quota.toast.aiTokens.warn.title': 'You are nearing your AI usage limit',
  'quota.toast.aiTokens.exceeded.title': 'AI usage limit reached',
  'quota.toast.cta': 'Upgrade to Pro',
  'aiUsage.title': 'Tokens used this month',
  'aiUsage.subtitle': 'Chat, insights, watch validation…',
  'aiUsage.tokensLabel': 'tokens',
  'aiUsage.budget': 'Monthly budget',
  'aiUsage.noBudget': 'No monthly limit set. Contact us to define one.',
  'aiUsage.warningHigh': 'You are close to your monthly limit.',
  'aiUsage.breakdown': 'Breakdown by feature',
  'aiUsage.calls': 'calls',
  'notifSettings.weeklyDigest.title': 'Weekly digest',
  'notifSettings.weeklyDigest.body':
    'A short email every Monday morning with the top insights to look at.',
  'notifSettings.criticalAlerts.title': 'Critical alerts',
  'notifSettings.criticalAlerts.body':
    'An immediate email whenever a critical insight is detected.',
  'notifSettings.emailOverride.title': 'Custom email',
  'notifSettings.emailOverride.body':
    'Override the default email. Leave empty to use your account email.',
  'notifSettings.emailOverride.placeholder': 'you@company.com',
  'notifSettings.save': 'Save',
  'notifSettings.saved': 'Saved ✓',
  'settings.section.dangerZone': 'Danger zone',
  'settings.field.fullName': 'Full name',
  'settings.field.userId': 'User ID',
  'settings.field.workspaceName': 'Name',
  'settings.field.workspaceId': 'Workspace ID',
  'settings.field.yourRole': 'Your role',
  'settings.password.title': 'Password',
  'settings.password.body': 'Reset your password via the email flow. We’ll send you a magic link.',
  'settings.password.button': 'Reset password',
  'settings.signOut.title': 'Sign out everywhere',
  'settings.signOut.body': 'End this session and invalidate the refresh token.',
  'settings.export.title': 'Download your data',
  'settings.export.body':
    'Get a JSON export of everything we have about you (workspaces, connectors, metrics, audit logs).',
  'settings.export.button': 'Download',
  'settings.export.busy': 'Preparing…',
  'settings.export.success': 'Export downloaded',
  'settings.export.empty':
    'Export downloaded — your workspace has no connectors or metrics yet, so the file is mostly empty.',
  'settings.delete.title': 'Delete my account',
  'settings.delete.body':
    'Permanently remove your account and the workspaces you own alone. Audit logs are kept anonymized (legal retention). This can’t be undone.',
  'settings.delete.confirmHint': 'Type DELETE MY ACCOUNT to enable the button.',
  'settings.delete.button': 'Delete account',
  'settings.delete.busy': 'Deleting…',
  'settings.language.body':
    'Change the language of the interface. Your choice is saved on this device.',
  'settings.section.tracking': 'Tracking — SmartTag',
  'settings.section.billing': 'Plan & billing',
  'settings.section.whiteLabel': 'Branding',
  'settings.section.apiKeys': 'API keys',
  // ━━━ White-label (cahier §3 Lot 4) ━━━
  'whiteLabel.title': 'Brand colors and logo',
  'whiteLabel.body':
    'Customize the look of generated reports and your workspace. Reflected on every report you export.',
  'whiteLabel.color': 'Brand color',
  'whiteLabel.color.invalid': 'Must be #RRGGBB',
  'whiteLabel.logo': 'Logo URL',
  'whiteLabel.logo.invalid': 'Must be a https:// URL',
  'whiteLabel.save': 'Save branding',
  'whiteLabel.saving': 'Saving…',
  'whiteLabel.saved': 'Saved',
  'whiteLabel.error': 'Save failed',
  // ━━━ API keys (cahier §3 Lot 4) ━━━
  'apiKeys.title': 'API keys',
  'apiKeys.body':
    'Generate keys to script access to your workspace data (export, automations, custom integrations).',
  'apiKeys.create': 'Generate key',
  'apiKeys.create.nameLabel': 'Key name',
  'apiKeys.create.namePlaceholder': 'e.g. Zapier prod, monthly export script',
  'apiKeys.creating': 'Generating…',
  'apiKeys.confirm': 'Generate',
  'apiKeys.cancel': 'Cancel',
  'apiKeys.error': 'Generation failed',
  'apiKeys.empty': 'No API keys yet. Generate one to get started.',
  'apiKeys.lastUsed': 'Last used',
  'apiKeys.revoked': 'Revoked',
  'apiKeys.revoke': 'Revoke',
  'apiKeys.revoke.confirm':
    'Revoke this key? Any script still using it will stop working immediately.',
  'apiKeys.revealed.warning': '⚠ Copy this key now — it will never be shown again.',
  'apiKeys.revealed.body':
    "Store it in a password manager or env variable. We only keep a hash, so we can't show it again.",
  'apiKeys.revealed.dismiss': "I've copied it, hide",
  'apiKeys.footer':
    "API key format: sa_test_…/sa_live_… · 32 base62 chars. Pass it as 'Authorization: Bearer <key>'.",
  // ━━━ Veille snooze (cahier §3 Lot 4) ━━━
  'watch.snooze.button': 'Snooze',
  'watch.snooze.24h': 'For 24h',
  'watch.snooze.7d': 'For 7 days',
  'watch.snooze.cancel': 'Resume now',
  'watch.snoozed.until': 'Snoozed until {{date}}',
  // ━━━ Team management (cahier §3 Lot 4) ━━━
  'settings.section.team': 'Team',
  'team.title': 'Members and invitations',
  'team.body':
    'Invite teammates to your workspace. Admins can manage billing and remove members; editors can connect sources and act on insights; viewers are read-only.',
  'team.invite.cta': 'Invite a member',
  'team.invite.email': 'Email address',
  'team.invite.role': 'Role',
  'team.invite.send': 'Send invitation',
  'team.invite.sending': 'Sending…',
  'team.invite.success': 'Invitation sent.',
  'team.invite.successNoEmail':
    'Invitation created — but the email could not be sent. Copy the accept link from the dashboard later.',
  'team.invite.error': 'Could not send invitation.',
  'team.invite.cancel': 'Cancel',
  'team.members.title': 'Active members',
  'team.members.empty': 'You are the only member.',
  'team.members.you': 'you',
  'team.members.remove': 'Remove',
  'team.members.remove.confirm': 'Remove this member from the workspace?',
  'team.invitations.title': 'Pending invitations',
  'team.invitations.empty': 'No pending invitations.',
  'team.invitations.expires': 'Expires {{date}}',
  'team.invitations.revoke': 'Revoke',
  'team.role.admin': 'Admin',
  'team.role.editor': 'Editor',
  'team.role.viewer': 'Viewer',
  'team.role.change': 'Change role',
  'team.error.lastAdmin': 'You cannot remove the last admin.',
  'team.error.selfRemove': "You can't remove yourself from this workspace.",
  'team.error.alreadyMember': 'This email is already a member of the workspace.',
  'team.error.invitePending': 'An invitation is already pending for this email.',
  // ━━━ Invite accept page ━━━
  'invite.accept.title': 'Accept invitation',
  'invite.accept.body': 'You were invited to join a SmartAnalyst workspace.',
  'invite.accept.cta': 'Accept and continue',
  'invite.accept.busy': 'Accepting…',
  'invite.accept.success': 'Welcome! Redirecting…',
  'invite.accept.error.generic': 'Could not accept this invitation.',
  'invite.accept.error.expired': 'This invitation has expired. Ask for a new one.',
  'invite.accept.error.notPending': 'This invitation was already used or revoked.',
  'invite.accept.error.emailMismatch':
    'This invitation was sent to a different email address. Sign in with that account first.',
  'invite.accept.signinFirst': 'You need to sign in to accept this invitation.',
  // ━━━ Command palette (Lot 4 enriched) ━━━
  'palette.section.go': 'Go to',
  'palette.section.actions': 'Quick actions',
  'palette.go.chat': 'Open chat',
  'palette.go.audit': 'Audit dashboard',
  'palette.go.veille': 'Watches',
  'palette.go.tasks': 'Tasks',
  'palette.go.reports': 'Reports',
  'palette.go.sources': 'Sources',
  'palette.go.live': 'Live activity',
  'palette.go.settings': 'Settings',
  'palette.action.newWatch': 'Create a watch',
  'palette.action.newReport': 'Generate a report',
  'palette.action.openBilling': 'Open billing',
  'palette.action.inviteMember': 'Invite a member',
  // ━━━ Reports analyst note + templates (Lot 4) ━━━
  'reports.gen.template': 'Template',
  'reports.gen.template.standard': 'Standard — full overview',
  'reports.gen.template.executive': 'Executive — score, top alerts and KPIs',
  'reports.gen.template.detail': 'Detail — KPIs by source + previous comparison',
  'reports.gen.template.agency': 'Agency — full white-label, brand colors',
  'reports.gen.aiNote': 'Generate an AI analyst note',
  'reports.gen.aiNote.hint':
    'Adds a 3-line commentary at the top of the report ("the take of the month").',
  'reports.analystNote.heading': 'The analyst’s take',
  // ━━━ Reports wizard (V2 — sources / period / context) ━━━
  'reports.wizard.step1': 'Sources',
  'reports.wizard.step2': 'Period',
  'reports.wizard.step3': 'Context',
  'reports.wizard.sourcesTitle': 'Which sources should the report cover?',
  'reports.wizard.sourcesAll': 'All sources are included by default — untick what you don’t want.',
  'reports.wizard.sourcesEmpty': 'Connect a data source first to generate a report.',
  'reports.wizard.sourcesEmptyCta': 'Connect a source',
  'reports.wizard.periodTitle': 'Which period?',
  'reports.wizard.preset.thisMonth': 'This month',
  'reports.wizard.preset.lastMonth': 'Last month',
  'reports.wizard.preset.last30': 'Last 30 days',
  'reports.wizard.preset.thisQuarter': 'This quarter',
  'reports.wizard.preset.custom': 'Custom',
  'reports.wizard.periodInvalid': 'The start date must be before the end date.',
  'reports.wizard.contextTitle': 'Anything the analysis should know? (optional)',
  'reports.wizard.contextPlaceholder':
    'e.g. “We ran a -20% promo from the 10th to the 15th” · “Focus on SEO” · “New site launched mid-month”',
  'reports.wizard.contextHint':
    'The AI weaves this context into its reading of the numbers and its recommendations.',
  'reports.wizard.modeLabel': 'Analysis',
  'reports.wizard.next': 'Next',
  'reports.wizard.back': 'Back',
  // ━━━ Billing (cahier §3 Lot 3) ━━━
  'billing.currentPlan': 'Current plan',
  'billing.active': 'Active',
  'billing.free.description':
    "You're on the Free plan: 1 source connected, 3 insights per month. Upgrade to Pro for unlimited.",
  'billing.pro.description':
    'Pro plan: unlimited sources, unlimited insights, reports and Deep mode chat.',
  'billing.upgradeToPro': 'Upgrade to Pro',
  'billing.upgradeToStarter': 'Or start with Starter',
  'billing.managePortal': 'Manage plan',
  'billing.loading': 'Opening…',
  'billing.usage': 'Usage this month',
  'billing.usage.connectors': 'Connected sources',
  'billing.usage.insights': 'Insights generated',
  'billing.usage.reports': 'Report generation',
  'billing.usage.deepChat': 'Deep mode chat',
  'billing.unlimited': '∞',
  'billing.loadError': "Couldn't load your billing details.",
  'billing.error.notConfigured': 'Billing is not yet configured. Contact support.',
  'billing.error.noCustomer': 'No subscription to manage yet — upgrade first.',
  'billing.error.planUnavailable': 'This plan is currently unavailable.',
  'billing.error.generic': 'Billing request failed. Try again.',
  'billing.grace.title': 'Payment failed',
  'billing.grace.body':
    'Your last payment did not go through. Update your payment method within {days} days to keep your current plan.',
  'billing.grace.expiredTitle': 'Plan suspended',
  'billing.grace.expiredBody':
    'Your payment method could not be charged and the grace period has ended. Your plan has been downgraded to Free. Update your payment method to reactivate.',
  'billing.grace.cta': 'Update payment method',
  // ━━━ UpgradePrompt ━━━
  'upgrade.title': 'Pro feature',
  'upgrade.cta': 'Upgrade to Pro',
  'upgrade.loading': 'Opening…',
  'upgrade.error': "Couldn't open checkout. Try again.",
  'upgrade.reason.connectors':
    "You've reached your 1-source limit. Pro lets you connect unlimited sources.",
  'upgrade.reason.insights':
    "You've reached your 3 insights/month. Pro removes the limit so you don't miss a signal.",
  'upgrade.reason.reports': 'Report generation is included in the Pro plan.',
  'upgrade.reason.deepChat':
    'Deep mode (longer, deeper analysis) is included in the Pro plan. Quick mode stays available.',
  'upgrade.reason.generic': 'This is a Pro feature.',
  'settings.section.privacy': 'Privacy',
  'settings.privacy.title': 'Cookie preferences',
  'settings.privacy.body':
    'Review or update your cookie choices for analytics and marketing. Strictly necessary cookies cannot be disabled.',
  'settings.privacy.button': 'Manage cookies',

  // ━━━ Consent banner ━━━
  'consent.title': 'Cookies & tracking',
  'consent.message':
    'We use cookies to keep you signed in and improve SmartAnalyst. Analytics and marketing are optional — you can change your mind anytime.',
  'consent.acceptAll': 'Accept all',
  'consent.rejectAll': 'Reject all',
  'consent.save': 'Save choice',
  'consent.privacyLink': 'Read our Privacy policy',
  'consent.cat.necessary.label': 'Strictly necessary',
  'consent.cat.necessary.desc':
    'Authentication, session, security. Required to use SmartAnalyst — always on.',
  'consent.cat.analytics.label': 'Analytics',
  'consent.cat.analytics.desc':
    'Helps us understand how the app is used so we can improve it. Aggregated, never sold.',
  'consent.cat.marketing.label': 'Marketing',
  'consent.cat.marketing.desc': 'Allows measuring our ad campaigns. Disabled by default.',

  // ━━━ Tracking (SmartTag block inside Settings) ━━━
  'tracking.writeKey.label': 'Write key',
  'tracking.writeKey.body':
    'Used by the tracking snippet to identify your workspace. Public-safe — can be exposed in client-side code.',
  'tracking.snippet.title': 'Install snippet',
  'tracking.snippet.body':
    'Paste this in the <head> of every page you want to track. 1.5 KB gzipped, async, zero cookies.',
  'tracking.cta.viewInstall': 'Full install guide',
  'tracking.cta.viewLive': 'See live events',
  'common.copy': 'Copy',
  'common.copied': 'Copied',

  // ━━━ Live polish (filters + sparkline) ━━━
  'live.filter.all': 'All',
  'live.filter.pageview': 'Pageviews',
  'live.filter.click': 'Clicks',
  'live.filter.error': 'Errors',
  'live.filter.session_start': 'Sessions',
  'live.filter.custom': 'Custom',
  'live.chart.title': 'Pageviews per minute',
  'live.chart.empty': 'Waiting for data…',

  // ━━━ Install guide page (/tracking/install) ━━━
  'install.kicker': 'Installation',
  'install.title': 'Install your tracking tag in 3 steps.',
  'install.subtitle':
    'Takes 2 minutes. Works with any CMS — WordPress, Shopify, Webflow, Ghost, or plain HTML.',
  'install.step1.title': '1. Copy your snippet',
  'install.step1.body': "It's already filled with your workspace write key. Just copy it.",
  'install.step2.title': "2. Paste it in your site's <head>",
  'install.step2.body': 'Pick your platform for the exact instructions:',
  'install.step2.tab.html': 'Plain HTML',
  'install.step2.tab.wordpress': 'WordPress',
  'install.step2.tab.shopify': 'Shopify',
  'install.step2.tab.webflow': 'Webflow',
  'install.step2.tab.ghost': 'Ghost',
  'install.step2.html.body':
    'Open your site files and paste the snippet just before </head> on every page (or in your shared template / layout file).',
  'install.step2.wordpress.body':
    'WordPress admin → Appearance → Theme File Editor → header.php → paste just before </head>. Or use the free plugin "Insert Headers and Footers": Settings → Insert Headers and Footers → paste in the "Scripts in Header" field.',
  'install.step2.shopify.body':
    'Shopify admin → Online Store → Themes → Actions → Edit code → Layout → theme.liquid → paste just before </head>. Click Save.',
  'install.step2.webflow.body':
    'Webflow → Project Settings → Custom Code → Head Code → paste the snippet → Save → re-publish your site.',
  'install.step2.ghost.body':
    'Ghost admin → Settings → Code injection → Site Header → paste the snippet → Save.',
  'install.step3.title': "3. Verify it's working",
  'install.step3.body':
    'Open your site in a new tab and click around. Then come back here and open the Live page — events should stream in within seconds.',
  'install.step3.cta': 'Open Live page',
  'install.faq.title': 'Frequently asked questions',
  'install.faq.q1': 'Do I need a cookie banner?',
  'install.faq.a1':
    "No. Events are tied to a session ID kept in sessionStorage (cleared when the tab closes). The tag sets no cookies, doesn't track across sessions or sites, and doesn't require consent for analytics under CNIL / PECR guidance. If you also load Google Analytics or Meta Pixel, those still need consent.",
  'install.faq.q2': 'Will it slow down my site?',
  'install.faq.a2':
    'The script is async (non-blocking) and 1.5 KB gzipped — smaller than a favicon. Events are sent via navigator.sendBeacon to never delay page unload. Zero impact on Core Web Vitals.',
  'install.faq.q3': 'What data is collected?',
  'install.faq.a3':
    'Page URLs (path + query keys only, no values), referrer origin only, click selectors (no text content), and any custom events you send via Smartanalyst("track", ...). Emails and phone numbers in custom properties are stripped server-side as a safety net.',
  'install.faq.q4': 'Can I send custom events?',
  'install.faq.a4':
    'Yes — Smartanalyst("track", "signup_completed", { plan: "pro" }). Property values must be strings, numbers, or booleans. Max 20 per event, strings truncated to 200 chars.',
  'install.faq.q5': 'How do I disable auto-pageviews (SPA)?',
  'install.faq.a5':
    'Pass disableAutoPageview: true to Smartanalyst("init", { ... }), then call Smartanalyst("page") manually after each route change.',

  // ━━━ Audit (SEO / GEO on-demand) ━━━
  'nav.audit': 'Audit',
  'audit.kicker': 'Audit',
  'audit.title': 'Audit your site in 5 seconds.',
  'audit.subtitle':
    'SEO + GEO (AI search readiness) + Performance (Core Web Vitals) + AI scoring (Claude evaluates citation-worthiness). 30+ checks in under 30 seconds.',
  'audit.form.url.label': 'URL to audit',
  'audit.form.url.placeholder': 'https://your-site.com',
  'audit.form.run': 'Run audit',
  'audit.form.running': 'Auditing…',
  'audit.form.runningHint': 'Loading the page and analyzing — takes 5 to 10 seconds.',
  'audit.score.label': 'Overall score',
  'audit.score.outOf': '/ 100',
  'audit.summary.pass': 'Pass',
  'audit.summary.warn': 'Warnings',
  'audit.summary.fail': 'Fails',
  'audit.summary.info': 'Info',
  'audit.section.seo': 'SEO findings',
  'audit.section.geo': 'GEO findings (AI search visibility)',
  'audit.section.perf': 'Performance (Core Web Vitals)',
  'audit.section.ai': 'AI readiness (citation by LLMs)',
  'audit.subscore.seo': 'SEO',
  'audit.subscore.geo': 'GEO',
  'audit.subscore.perf': 'Perf',
  'audit.subscore.ai': 'AI',
  'audit.cwv.title': 'Core Web Vitals',
  'audit.skipped.title': 'Analyzer skipped',
  'audit.ai.valueProp': 'Value prop',
  'audit.ai.citation': 'Citation-worthy',
  'audit.ai.qa': 'Q&A struct.',
  'audit.ai.jargon': 'Jargon (less is better)',
  'audit.ai.coherence': 'Title coherence',
  'audit.ai.strengths': 'Strengths',
  'audit.ai.weaknesses': 'Weaknesses',
  'audit.ai.verdict': 'AI verdict',

  // ━━━ Smart tag dashboard (tabs Analytics / SEO / GEO / Perf / AI) ━━━
  'dash.tab.analytics': 'Analytics',
  'dash.tab.seo': 'SEO',
  'dash.tab.geo': 'GEO',
  'dash.tab.perf': 'Performance',
  'dash.tab.ai': 'AI readiness',
  'dash.audit.lastRun': 'Last audit',
  'dash.audit.rerun': 'Re-run audit',
  'dash.audit.rerunning': 'Running…',
  'dash.audit.openFull': 'Open full report',
  'dash.audit.findings': 'Findings',
  'dash.audit.sectionMissing':
    'This section is not available in the last audit. Try re-running it.',
  'dash.audit.perfSkipped':
    'Performance analyzer needs a Google PageSpeed API key on the API server.',
  'dash.audit.aiSkipped': 'AI readability analyzer needs an Anthropic API key on the API server.',
  'dash.audit.empty.title': 'Run your first audit',
  'dash.audit.empty.body':
    'Get a 4-axis report (SEO, AI search, performance, citation-worthiness) on any URL in under 30 seconds.',
  'dash.audit.empty.placeholder': 'https://your-site.com',
  'dash.audit.empty.cta': 'Run audit',
  'dash.audit.empty.running': 'Auditing…',
  'audit.severity.pass': 'OK',
  'audit.severity.warn': 'Warn',
  'audit.severity.fail': 'Fail',
  'audit.severity.info': 'Info',
  'audit.findings.recommendation': 'Recommendation',
  'audit.history.title': 'Recent audits',
  'audit.history.empty': 'No audit yet — run your first one above.',
  'audit.history.open': 'Open',
  'audit.error.invalid_url': "This URL doesn't look valid. Use https://example.com.",
  'audit.error.scrape_failed':
    "We couldn't reach the site (timeout, DNS, or SSL error). Check the URL and try again.",
  'audit.error.generic': 'Something went wrong running the audit. Try again.',
  'audit.finalUrl': 'Final URL after redirects',
  'audit.httpStatus': 'HTTP status',
  // ━━━ ErrorBoundary ━━━
  'error.boundary.badge': 'Unexpected error',
  'error.boundary.title': 'Something went wrong.',
  'error.boundary.body': 'The issue has been noted. Refresh the page or go home to continue.',
  'error.boundary.refresh': 'Refresh',
  'error.boundary.home': 'Go home',
} as const

const fr: Record<keyof typeof en, string> = {
  // ━━━ Page d'aide (next-steps D) ━━━
  'help.topbar.title': 'Aide',
  'help.topbar.subtitle': "Tout ce qu'il faut pour utiliser SmartAnalyst",
  'help.eyebrow': 'Documentation',
  'help.title': 'Bienvenue dans SmartAnalyst',
  'help.intro':
    'Des guides concrets pour tout ce que tu peux faire ici : connecter une source, poser une question, générer un rapport, épingler un widget. Rien de plus, rien de moins — si tu ne trouves pas la réponse ici, écris au support.',
  'help.toc.title': 'Dans cette page',
  'help.toc.aria': 'Sommaire',
  // Premiers pas
  'help.gs.title': 'Démarrer en 3 étapes',
  'help.gs.body1':
    "Après l'inscription, le chemin le plus rapide vers la valeur c'est : connecte une source, pose une question. Moins de 5 minutes.",
  'help.gs.step1': 'Connecte une source',
  'help.gs.step1.sub': 'GA4 est le point de départ le plus courant (OAuth, 30 secondes).',
  'help.gs.step2': 'Attends le 1er brief',
  'help.gs.step2.sub':
    "On synchronise 30 jours d'historique. La page d'accueil se remplit automatiquement.",
  'help.gs.step3': 'Pose ta 1ère question',
  'help.gs.step3.sub': 'Utilise les quick-cards du chat pour découvrir ce qui est possible.',
  // Sources
  'help.sources.title': 'Sources connectées',
  'help.sources.body':
    'SmartAnalyst se branche à tes outils marketing via OAuth (ou token API). Tous les connecteurs sont en lecture seule — on ne modifie jamais tes données côté source.',
  'help.sources.ga4': 'Web analytics (sessions, conversions, canaux)',
  'help.sources.meta_ads': 'Publicités Facebook + Instagram (spend, ROAS, campagnes)',
  'help.sources.google_ads': 'Search + Display Ads',
  'help.sources.stripe': 'Abonnements, MRR, churn, revenus',
  'help.sources.search_console': 'Recherche organique (requêtes, CTR, position)',
  'help.sources.smarttag': 'Tracking first-party installé via snippet JS',
  'help.sources.callout':
    'Un connecteur en erreur ? La page "Sources" montre la santé de chaque connecteur avec un re-auth en 1 clic.',
  // Chat
  'help.chat.title': 'Le chat copilote IA',
  'help.chat.body':
    "Le chat ne fait pas que du texte — il génère des outputs structurés (charts, tables, funnels) et te permet d'agir dessus (export, épingle, slide deck).",
  'help.chat.outputs.title': 'Les outputs génératifs',
  'help.chat.out.kpi': 'Carte KPI',
  'help.chat.out.kpi.desc': 'Un chiffre + delta vs période précédente, avec mini sparkline.',
  'help.chat.out.chart': 'Graphique',
  'help.chat.out.chart.desc': 'Série temporelle journalière / hebdomadaire, en barres ou ligne.',
  'help.chat.out.table': 'Table de comparaison',
  'help.chat.out.table.desc': 'N items classés par une métrique (top campagnes, top canaux…).',
  'help.chat.out.compare': 'Comparaison',
  'help.chat.out.compare.desc':
    'Deux sources côte à côte (ex. GA4 vs Meta) avec échelle Y partagée.',
  'help.chat.out.funnel': 'Funnel',
  'help.chat.out.funnel.desc': 'Conversion multi-étapes avec rétention % entre chaque étape.',
  'help.chat.out.dashboard': 'Aperçu dashboard',
  'help.chat.out.dashboard.desc': '4-6 cartes KPI épinglables avec delta vs N-1.',
  'help.chat.actions.title': 'Les actions sur chaque réponse',
  'help.chat.actions.excel': 'Excel / CSV',
  'help.chat.actions.excel.desc': 'Téléchargement .xlsx réel (1 feuille par bloc de données).',
  'help.chat.actions.pin': 'Épingler',
  'help.chat.actions.pin.desc': "Sauvegarde le widget sur ton dashboard d'accueil (plan Pro).",
  'help.chat.actions.slides': 'Slides',
  'help.chat.actions.slides.desc': 'Deck .pptx, une slide par insight (plan Pro).',
  'help.chat.actions.rerun': 'Rejouer',
  'help.chat.actions.rerun.desc':
    'Re-pose la même question avec le mode opposé (Rapide ↔ Approfondi).',
  // Rapports
  'help.reports.title': 'Rapports',
  'help.reports.body':
    "Génère un rapport mensuel / trimestriel / sur période libre en HTML, avec white-label optionnel, mot de l'analyste écrit par l'IA, et split par source. Imprime en PDF directement depuis le navigateur.",
  'help.reports.callout': "L'envoi auto le 1er du mois se configure dans Réglages → Notifications.",
  // Veilles
  'help.watches.title': 'Veilles (alertes custom)',
  'help.watches.body':
    'Pose des règles pour être prévenu quand une métrique franchit un seuil. Le Watch Evaluator tourne toutes les heures et notifie en in-app ou email si la règle se déclenche.',
  'help.watches.op.drops_below': '« Tombe sous X » — se déclenche quand valeur < seuil.',
  'help.watches.op.rises_above': '« Dépasse X » — se déclenche quand valeur > seuil.',
  'help.watches.op.changes_by_pct':
    '« Varie de ±N % » — se déclenche sur grosse variation jour-à-jour.',
  'help.watches.op.any_change':
    '« Tout changement » — utile pour les métriques sentinelles type statut connecteur.',
  // Pinned
  'help.pinned.title': 'Widgets épinglés',
  'help.pinned.body':
    "Depuis n'importe quel KPI ou chart du chat, clique sur 📌 pour l'épingler sur ton dashboard d'accueil. Jusqu'à 12 épingles par workspace. Plan Pro uniquement.",
  // Plans
  'help.plans.title': 'Plans et quotas',
  'help.plans.col.feature': 'Fonctionnalité',
  'help.plans.row.price': 'Prix mensuel',
  'help.plans.row.connectors': 'Connecteurs actifs',
  'help.plans.row.insights': 'Insights / mois',
  'help.plans.row.reports': 'Génération de rapports',
  'help.plans.row.deep': 'Mode Approfondi (chat)',
  'help.plans.row.pin': 'Épingler au dashboard',
  'help.plans.row.slides': 'Export deck de slides',
  'help.plans.foot': 'Gère ton plan dans',
  // Raccourcis
  'help.shortcuts.title': 'Raccourcis clavier',
  'help.shortcuts.search': 'Ouvrir la recherche globale / palette de commandes',
  'help.shortcuts.send': 'Envoyer le message chat en cours',
  'help.shortcuts.close': "Fermer n'importe quel modal ouvert",
  // Contact
  'help.contact.title': "Tu n'as pas trouvé ce que tu cherchais ?",
  'help.contact.body': 'Écris-nous — on lit tout et on répond sous 24h en jours ouvrés :',
  // ━━━ /Help page fin ━━━
  // ━━━ Bar de progression activation (next-steps F) ━━━
  'activation.aria': 'Progression onboarding',
  'activation.label': 'Pour démarrer',
  'activation.step.connect': 'Connecter une source',
  'activation.step.ask': 'Poser la 1ère question',
  'activation.step.pin': 'Épingler le 1er insight',
  'activation.dismiss': 'Masquer',
  // ━━━ Common ━━━
  'common.signOut': 'Se déconnecter',
  'common.email': 'Email',
  'common.password': 'Mot de passe',
  'common.you': 'Toi',
  'common.or': 'ou',
  'common.loading': 'Chargement…',
  'common.back': 'Retour',
  'common.backToSite': 'Retour sur smartanalyst.io',
  'common.delete': 'Supprimer',
  'common.close': 'Fermer',

  // ━━━ Nav / layout (App v2 — direction « Le Brief ») ━━━
  'nav.workspace': 'Espace',
  'nav.noWorkspace': 'Aucun espace',
  'nav.dashboard': 'Tableau de bord',
  'nav.live': 'Smart tag',
  'nav.menu': 'Menu',
  'nav.openMenu': 'Ouvrir le menu',
  'nav.closeMenu': 'Fermer le menu',
  'nav.connectors': 'Connecteurs',
  'nav.chat': 'Chat',
  'nav.tasks': 'Tâches',
  'nav.home': 'Mon point du jour',
  'nav.assistant': 'Assistant',
  'nav.veille': 'Veille',
  'nav.todo': 'À faire',
  'nav.sources': 'Sources',
  'nav.mySpace': 'Mon espace',
  'nav.yourBusiness': 'Ton business',
  'nav.expand': 'Ouvrir le panneau',
  'nav.collapse': 'Réduire le panneau',
  'nav.dataUpToDate': 'Données à jour',
  'nav.askQuestion': 'Question',

  // ━━━ Brief home (App v2 — accueil narré) ━━━
  'brief.topbar.title': 'Mon point du jour',
  'brief.greeting': 'Bonjour {{name}}',
  'brief.score.label': 'santé',
  'brief.score.good': 'Ça va plutôt bien',
  'brief.score.mid': 'Signaux mitigés',
  'brief.score.low': 'À surveiller',
  'brief.score.unknown': 'Pas encore assez de données',
  'brief.score.noData':
    'Connecte une source de données et je commence à scorer ton business en quelques minutes.',
  'brief.narrative.up':
    'Ton score progresse de {{delta}} points cette semaine — la dynamique est avec toi.',
  'brief.narrative.down':
    'Ton score perd {{delta}} points cette semaine — je te montre ce qui a changé.',
  'brief.narrative.flat': 'Ton score est stable cette semaine.',
  'brief.narrative.one': 'Il y a une chose à régler en priorité.',
  'brief.narrative.many': 'Il y a {{n}} choses à regarder en priorité.',
  'brief.narrative.allGood': 'Rien de critique à signaler — continue comme ça.',
  'brief.three.title': 'Les 3 choses qui comptent ce matin',
  'brief.three.body': 'Chaque point : ce qui se passe, pourquoi, et ce que je te recommande.',
  'brief.three.empty':
    'Rien de critique pour le moment. Je remonte les éléments dès que je les détecte.',
  'brief.three.reco': 'Ma reco',
  'brief.three.cta': 'Voir toute ma veille',
  'brief.three.toTasks': 'Ajouter à mes tâches',
  'brief.three.added': 'Ajoutée',
  'brief.three.askDetails': 'Demander des détails',
  'brief.proposed.title': 'Ce que je te propose de faire',
  'brief.proposed.body': "Valide ce qui te parle, je m'occupe du reste.",
  'brief.proposed.empty': 'Aucune proposition en attente. Reviens demain matin.',
  'brief.proposed.cta': 'Toutes mes tâches',
  'brief.proposed.toCurate': 'à curer',
  'brief.breakdown.title': 'Santé par dimension',
  'brief.breakdown.paid': 'Payant',
  'brief.breakdown.organic': 'Organique',
  'brief.breakdown.conversion': 'Conversion',
  'brief.breakdown.revenue': 'Revenu',
  'brief.breakdown.tracking': 'Tracking',
  'brief.kpis.eyebrow': "En un coup d'œil",
  'brief.kpis.empty': 'Connecte une source pour voir tes KPIs ici.',
  'briefHome.pinned.title': 'Épinglé depuis le chat',
  'brief.ask.body': "Une question sur tes chiffres ? Demande-moi, j'ai tout sous la main.",
  'brief.ask.cta': "Ouvrir l'assistant",
  'brief.report.cta': 'Générer un rapport',
  'brief.onboardCta.title': 'Configure ton espace en 5 minutes.',
  'brief.onboardCta.body':
    "J'analyse ton site, je détecte ton secteur et je branche ta première source.",
  'brief.onboardCta.cta': "Lancer l'onboarding",
  'brief.firstInsight.toast': 'Ton premier insight vient de tomber !',
  // First-run state (workspace sans canonical_metrics)
  'brief.firstRun.eyebrow': 'BIENVENUE',
  'brief.firstRun.title': 'Salut {{name}} — on installe le terrain.',
  'brief.firstRun.body':
    "Pour pouvoir te briefer chaque matin sur ce qui a bougé, j'ai besoin qu'au moins une source soit branchée. Ça prend 30 secondes — pars de l'outil que tu regardes le plus.",
  'brief.firstRun.cta': 'Brancher une source',
  'brief.firstRun.tour': 'Visite guidée',
  'brief.firstRun.tourSub': "J'analyse ton site, je te propose un profil et je t'accompagne.",
  'brief.firstRun.ask': "Essayer l'assistant",
  'brief.firstRun.askSub': 'Pose une question tout de suite — je te montre ce que je sais faire.',

  // ━━━ Onboarding (App v2 — overlay 5 étapes §3.7C) ━━━
  'onboarding.flowTitle': 'Onboarding',
  'onboarding.step': 'ÉTAPE',
  'onboarding.exit': 'Quitter',
  'onboarding.exit.confirm':
    'Sûr de vouloir quitter ? Ta progression sera perdue. Tu pourras relancer plus tard depuis ta home.',
  'onboarding.error.url': 'URL invalide. Elle doit commencer par http(s)://.',
  'onboarding.error.generic': "Quelque chose s'est mal passé. Réessaie.",
  'onboarding.s1.hint': '~ 20 secondes',
  'onboarding.s1.title': "Quelle est l'adresse de ton site ?",
  'onboarding.s1.body':
    "Je vais l'analyser pour comprendre ton business, ton secteur et les outils que tu utilises. Tu pourras tout corriger ensuite.",
  'onboarding.s1.cta': 'Analyser mon site',
  'onboarding.s1.analyzing': 'Analyse en cours…',
  'onboarding.s2.hint': 'Profil détecté',
  'onboarding.s2.title': "J'ai compris ton business",
  'onboarding.s2.body': "Voici ce que j'ai détecté depuis",
  'onboarding.s2.activity': 'ACTIVITÉ',
  'onboarding.s2.sector': 'SECTEUR',
  'onboarding.s2.market': 'MARCHÉ',
  'onboarding.s2.tools': 'OUTILS',
  'onboarding.s2.keywords': 'MOTS-CLÉS',
  'onboarding.s2.correct': 'Corriger',
  'onboarding.s2.confirm': "C'est exact, continuer",
  'onboarding.s2.saving': 'Sauvegarde…',
  // Step 3 — quiz maturité
  'onboarding.s3.hint': 'Ton profil',
  'onboarding.s3.title': "Quelques questions pour mieux t'aider",
  'onboarding.s3.body': "Ça me permet d'adapter les insights, actions et priorités à ta réalité.",
  'onboarding.s3.businessModel': 'Modèle commercial',
  'onboarding.s3.businessModel.b2b': 'B2B',
  'onboarding.s3.businessModel.b2c': 'B2C',
  'onboarding.s3.businessModel.b2b2c': 'B2B2C',
  'onboarding.s3.businessModel.marketplace': 'Marketplace',
  'onboarding.s3.businessModel.other': 'Autre',
  'onboarding.s3.maturityLevel': 'Ta maturité marketing',
  'onboarding.s3.maturityLevel.beginner': 'Je débute',
  'onboarding.s3.maturityLevel.beginner.desc': "Peu d'outils, suivi principalement manuel",
  'onboarding.s3.maturityLevel.intermediate': 'En croissance',
  'onboarding.s3.maturityLevel.intermediate.desc': 'GA4, pubs en cours, quelques automatisations',
  'onboarding.s3.maturityLevel.advanced': 'Avancé',
  'onboarding.s3.maturityLevel.advanced.desc': 'Multi-canal, attribution, CRM connecté',
  'onboarding.s3.primaryGoal': 'Ce qui compte le plus maintenant ?',
  'onboarding.s3.primaryGoal.grow_revenue': "Augmenter le chiffre d'affaires",
  'onboarding.s3.primaryGoal.reduce_cac': "Réduire le coût d'acquisition",
  'onboarding.s3.primaryGoal.improve_retention': 'Améliorer la rétention',
  'onboarding.s3.primaryGoal.optimize_campaigns': 'Optimiser les campagnes',
  'onboarding.s3.primaryGoal.understand_customers': 'Comprendre mes clients',
  'onboarding.s3.currentStack': "Outils que tu utilises aujourd'hui",
  'onboarding.s3.currentStack.placeholder': 'Sélectionne les outils que tu utilises actuellement',
  'onboarding.s3.cta': 'Continuer',
  'onboarding.s3.saving': 'Sauvegarde…',
  'onboarding.s4.hint': 'Connexion en 1 clic',
  'onboarding.s4.title': 'Connecte ta première source',
  'onboarding.s4.body': 'Pour ton activité, je te recommande de commencer par',
  'onboarding.s4.desc': 'Ventes, produits, clients — lecture seule',
  'onboarding.s4.recommended': 'Recommandé',
  'onboarding.s4.connect': 'Connecter Shopify',
  'onboarding.s4.later': 'Je connecterai plus tard',
  'onboarding.s4.fallback':
    "Je n'ai pas pu lire le site automatiquement — mode manuel. Choisis la source que tu regardes le plus, on partira de là.",
  'onboarding.s5.title': 'Je récupère tes 30 derniers jours…',
  'onboarding.s5.body':
    'Calcul de ton score de santé et des premiers signaux. Généralement moins d’une minute.',
  'onboarding.s5.check1': 'Connexion en place',
  'onboarding.s5.check2': 'Score de santé calculé',
  'onboarding.s5.check3': 'Premiers signaux extraits',
  'onboarding.s5.slow': "C'est un peu plus long que d'habitude — on continue.",
  'onboarding.s5.error': 'Petit ralenti, on ouvre quand même ton espace.',
  'onboarding.s5.live': 'Chargement de ta première analyse',
  'onboarding.s6.done': 'Analyse terminée',
  'onboarding.s6.title': "Voici ce que j'ai déjà repéré.",
  'onboarding.s6.body':
    'Ton score de santé de départ et les premiers signaux sur tes vraies données.',
  'onboarding.s6.cardTitle': 'Bon départ.',
  'onboarding.s6.cardTitleNamed': 'Bon départ, {{name}}.',
  'onboarding.s6.cardBody':
    "Ton acquisition est solide. Ton premier levier : la conversion. On va s'en occuper ensemble.",
  'onboarding.s6.cardBodyNoData':
    "J'aurai plus à dire dès que tes données seront synchronisées. En attendant, voici ton espace.",
  'onboarding.s6.errorNoWorkspace':
    "Ton compte n'est pas tout à fait prêt. Rafraîchis la page, ou contacte le support si ça persiste.",
  'onboarding.s6.cta': 'Entrer dans mon espace',

  // ━━━ Veille (App v2 — surveillance proactive §3.3) ━━━
  'veille.topbar.title': 'Ma veille',
  'veille.subtitle': 'Surveillance continue · résumé hebdo le lundi',
  'veille.title': 'Ta veille',
  'veille.intro':
    "Je surveille tes données en continu et je te préviens AVANT que tu n'aies à demander. Chaque signal arrive avec sa cause et une reco.",
  'veille.createAlert': 'Créer une alerte',
  'veille.filter.all': 'Tous',
  'veille.filter.critical': 'Alertes',
  'veille.filter.warning': 'À surveiller',
  'veille.filter.opportunity': 'Opportunités',
  'veille.filter.resolved': 'Traités',
  'veille.sev.critical': 'Alerte',
  'veille.sev.high': 'Alerte',
  'veille.sev.medium': 'À surveiller',
  'veille.sev.low': 'Opportunité',
  'veille.cause': 'CAUSE',
  'veille.reco': 'RECO',
  'veille.actions.addToTasks': 'Ajouter aux tâches',
  'veille.actions.askAssistant': "Demander à l'assistant",
  'veille.actions.ignore': 'Ignorer',
  'veille.actions.snooze': 'Reporter',
  'veille.snooze.oneHour': 'Pendant 1h',
  'veille.snooze.oneDay': "Jusqu'à demain",
  'veille.snooze.oneWeek': 'Pendant 1 semaine',
  'veille.askPrefix': "Dis-m'en plus",
  'veille.toast.addedToTasks': 'Ajouté à tes tâches',
  'veille.toast.ignored': 'Insight ignoré',
  'veille.toast.snoozed': 'Reporté — je le ramènerai plus tard',
  'veille.recap.title': 'Résumé hebdomadaire',
  'veille.recap.allGood': 'Rien de critique cette semaine. Continue, je veille.',
  'veille.recap.criticalCount': '{{n}} alerte(s) à traiter',
  'veille.recap.warningCount': '{{n}} point(s) à surveiller',
  'veille.recap.opportunityCount': '{{n}} opportunité(s)',
  'veille.empty.allClear': "Rien sur le radar pour l'instant — je remonte tout dès que ça bouge.",
  // First-run state (no canonical_metrics yet)
  'veille.firstRun.eyebrow': 'VEILLE',
  'veille.firstRun.title': 'Dis-moi quoi surveiller.',
  'veille.firstRun.body':
    "Une veille = une condition que je surveille pour toi (ROAS qui chute, CPA qui monte, churn qui spike…). Je te préviens dès qu'elle se déclenche vraiment. Pioche une idée ou écris la tienne — mais d'abord, branche au moins une source.",
  'veille.firstRun.cta': 'Brancher une source',
  'veille.firstRun.template.roas': 'Surveiller la chute du ROAS',
  'veille.firstRun.template.roasSub':
    '« Préviens-moi quand mon ROAS Meta passe sous 2 pendant 3 jours »',
  'veille.firstRun.template.cpa': 'Surveiller le CPA qui monte',
  'veille.firstRun.template.cpaSub': '« Préviens-moi quand mon CPA Google dépasse 30 € »',
  'veille.firstRun.template.churn': 'Surveiller le churn qui spike',
  'veille.firstRun.template.churnSub': '« Alerte-moi quand mon churn mensuel dépasse 5 % »',
  'veille.empty.resolved': 'Aucun insight traité pour le moment.',
  // Onglets top : insights IA vs veilles programmées
  'veille.tab.insights': 'Insights',
  'veille.tab.watches': 'Mes veilles',
  // Watches list (mes veilles : toggle on/off + delete)
  'veille.watchesList.title': 'Mes veilles',
  'veille.watchesList.activeSuffix': 'actives',
  'veille.watchesList.empty':
    "Tu n'as pas encore programmé de veille. Crées-en une pour être prévenu quand une métrique bouge.",
  'veille.watchesList.disable': 'Mettre en pause',
  'veille.watchesList.enable': 'Réactiver',
  'veille.watchesList.delete': 'Supprimer cette veille',
  'veille.watchesList.deleteConfirm': 'Supprimer cette veille ? Cette action est irréversible.',
  'veille.watchesList.deleted': 'Veille supprimée.',
  'veille.watchesList.deleteError': 'Impossible de supprimer cette veille.',
  'veille.watchesList.toggleError': 'Impossible de mettre à jour cette veille.',
  'veille.watchesList.triggered': 'déclenchée(s)',

  // ━━━ Watch modal (App v2 — création d'alerte custom §3.3) ━━━
  'watchModal.title': 'Créer une alerte',
  'watchModal.step': 'ÉTAPE',
  'watchModal.back': 'Retour',
  'watchModal.next': 'Continuer',
  'watchModal.create': "Créer l'alerte",
  'watchModal.creating': 'Création…',
  'watchModal.toast.created': "Alerte créée — je te préviens dès qu'elle se déclenche",
  'watchModal.toast.failed': 'Échec de la création. Réessaie.',
  'watchModal.s1.label': 'Que veux-tu que je surveille ?',
  'watchModal.s1.placeholder': 'ex : Préviens-moi si mon ROAS descend sous 2',
  'watchModal.s1.hint': 'Dis-le en français naturel, je trouve la bonne métrique.',
  'watchModal.s1.askToValidate': "Demande à l'assistant de confirmer.",
  'watchModal.s1.askButton': "Demander à l'assistant",
  'watchModal.s1.validating': 'Je lis…',
  'watchModal.s1.validated': 'Validé — meilleure correspondance : {{metric}}',
  'watchModal.s1.conf.high': "C'est compris — interprétation sûre",
  'watchModal.s1.conf.medium': "C'est presque ça — vérifie à l'étape suivante",
  'watchModal.s1.conf.low': "Je n'ai pas pu interpréter — choisis la métrique à l'étape suivante",
  'watchModal.s1.error': "Impossible de joindre l'assistant. Tu peux continuer manuellement.",
  'watchModal.s2.metric': 'Métrique',
  'watchModal.s2.operator': 'Condition',
  'watchModal.s2.threshold': 'Seuil',
  'watchModal.op.drops_below': 'descend sous',
  'watchModal.op.rises_above': 'dépasse',
  'watchModal.op.changes_by_pct': 'varie de % (jour sur jour)',
  'watchModal.op.any_change': 'tout changement',
  'watchModal.s3.channels': 'Comment je te préviens ?',
  'watchModal.s3.inApp': 'In-app (cloche)',
  'watchModal.s3.email': 'Email',
  'watchModal.s3.summary': 'Récapitulatif',
  'watchModal.s3.if': 'Si',
  'watchModal.summary.dropsBelow': 'descend sous',
  'watchModal.summary.risesAbove': 'dépasse',
  'watchModal.summary.changesByPct': 'varie de {{n}}% jour sur jour',
  'watchModal.summary.anyChange': "change (dans un sens ou l'autre)",

  // ━━━ Reports (App v2 — synthèse mensuelle §3.6) ━━━
  'reports.topbar.title': 'Mes rapports',
  'reports.subtitle': 'Synthèse mensuelle · pour toi ou à partager',
  'reports.generate': 'Générer un rapport',
  'reports.empty': "Aucun rapport pour l'instant. Génère-en un pour le mois en cours.",
  'reports.selectOne': 'Sélectionne un rapport pour l’aperçu.',
  // First-run state (no report yet)
  'reports.firstRun.eyebrow': 'RAPPORTS',
  'reports.firstRun.title': 'Lance ton premier rapport.',
  'reports.firstRun.body':
    "Un rapport = une synthèse propre et partageable de ton mois : score de santé, KPI, ce qui a compté, quoi faire. Choisis une période, je m'occupe du reste en 30 secondes.",
  'reports.firstRun.cta': 'Générer maintenant',
  'reports.printPdf': 'Imprimer en PDF',
  'reports.printBlocked': 'Pop-up bloquée. Autorise les pop-ups pour imprimer.',
  'reports.notReady': "Le rapport n'est pas encore prêt.",
  'reports.failed': 'Échec de génération. Réessaie.',
  'reports.deleteConfirm': 'Supprimer ce rapport ? Action irréversible.',
  'reports.toast.generated': 'Rapport généré',
  'reports.toast.failed': 'Échec de la génération.',
  'reports.toast.deleted': 'Rapport supprimé',
  'reports.status.draft': 'Brouillon',
  'reports.status.generating': 'En cours',
  'reports.status.ready': 'Prêt',
  'reports.status.sent': 'Envoyé',
  'reports.status.failed': 'Échec',
  'reports.kind.monthly': 'Mensuel',
  'reports.kind.quarterly': 'Trimestriel',
  'reports.kind.custom': 'Période libre',
  'reports.gen.title': 'Générer un rapport',
  'reports.gen.kind': 'Type',
  'reports.gen.start': 'Début',
  'reports.gen.end': 'Fin',
  'reports.gen.whiteLabel': 'White-label (sans branding SmartAnalyst)',
  'reports.gen.advanced': 'Avancé — sources, segmentation, comparaison',
  'reports.gen.sources': 'Sources à inclure',
  'reports.gen.sourcesNone': 'Aucun connecteur actif pour le moment.',
  'reports.gen.sourcesHint': 'Vide = toutes les sources connectées sont incluses.',
  'reports.gen.segmentBy': 'Segmenter les KPIs par',
  'reports.gen.segmentNone': 'Pas de segmentation (totaux)',
  'reports.gen.segmentSource': 'Par source (mini-tableau sous chaque KPI)',
  'reports.gen.compare': 'Comparer à la période précédente (même durée)',
  'reports.gen.cta': 'Générer',
  'reports.gen.generating': 'Génération…',

  // ━━━ Sources (App v2 — connecteurs + librairie §3.7) ━━━
  'sources.topbar.title': 'Mes sources de données',
  'sources.subtitle': 'Connecteurs live et librairie de fichiers',
  'sources.title': 'Tes sources de données',
  'sources.intro':
    "Les connecteurs alimentent ton tableau de bord en continu. La librairie, c'est ta matière première pour l'assistant — tout ce que tu y déposes devient interrogeable.",
  'sources.tab.connectors': 'Connecteurs live',
  'sources.tab.library': 'Librairie de fichiers',
  'sources.counters.active': '{{n}} actif(s)',
  'sources.counters.reconnect': '{{n}} à reconnecter',
  'sources.addSource': 'Ajouter une source',
  'sources.recommended': 'Recommandé pour ton activité',
  'sources.reconnect': 'Reconnecter',
  'sources.neverSynced': 'jamais synchronisé',
  'sources.status.synced': 'Synchro',
  'sources.status.expired': 'Expiré',
  'sources.health.healthy': 'Opérationnel',
  'sources.health.stale': 'Sync en retard',
  'sources.health.expired': 'Reconnexion requise',
  'sources.health.failing': 'Sync en échec',
  'sources.health.unknown': 'En attente',
  // ─── États d'échec d'activation (cahier §3 Lot 0 / §4.1 / §4.7) ─────
  'activation.error.oauth_error.title': 'Impossible de finaliser la connexion {{source}}',
  'activation.error.oauth_error.body':
    "Le provider a renvoyé une erreur pendant le handshake OAuth. Rien n'a été enregistré — tu peux retenter.",
  'activation.error.oauth_provider_denied.title': "L'accès n'a pas été accordé",
  'activation.error.oauth_provider_denied.body':
    "Toi — ou quelqu'un sur le compte — as refusé la permission. On en a besoin pour lire tes données ; choisis un autre compte ou redonne l'accès pour retenter.",
  'activation.error.scope_mismatch.title': 'Permissions partielles accordées sur {{source}}',
  'activation.error.scope_mismatch.body':
    "Certaines métriques resteront vides tant que l'accès complet n'est pas donné. Reconnecte pour corriger — ou continue avec ce qu'on a.",
  'activation.error.insufficient_data.title': 'Pas encore assez de données',
  'activation.error.insufficient_data.body':
    "Ton compte est connecté, mais il y a moins de 7 jours de données exploitables. On commence à produire des insights dès qu'on en a assez.",
  'activation.error.workspace_not_ready.title': "Ton workspace n'est pas tout à fait prêt",
  'activation.error.workspace_not_ready.body':
    'On finit la mise en place de ton compte. Rafraîchis dans une minute — si ça persiste, contacte le support.',
  'activation.error.unknown.title': "Quelque chose n'est pas passé",
  'activation.error.unknown.body':
    "On n'a pas pu confirmer ce qui s'est joué. Tu peux retenter ou revenir plus tard — rien n'est cassé de ton côté.",
  'activation.cta.retry': 'Retenter',
  'activation.cta.reconnect': 'Reconnecter',
  'activation.cta.dismiss': 'Fermer',
  'activation.cta.contactSupport': 'Contacter le support',
  'sources.empty.connectors':
    'Aucune source connectée. Branche GA4, Meta Ads ou Stripe en quelques clics — ton tableau de bord se remplira tout seul.',
  'sources.dropzone.title': 'Dépose un fichier ici',
  'sources.dropzone.body': "CSV, Excel, PDF ou image — réutilisable dans l'assistant",
  'sources.dropzone.browse': 'Parcourir mes fichiers',
  'sources.dropzone.uploading': 'Upload en cours…',
  'sources.empty.files':
    "Aucun fichier pour le moment. Dépose un CSV, un Excel, un PDF ou une image pour l'interroger.",
  'sources.askAbout': 'Interroger',
  'sources.toast.uploaded': 'Fichier uploadé',
  'sources.toast.uploadFailed': "Échec de l'upload",
  'sources.toast.deleted': 'Fichier supprimé',

  // ━━━ Chat ━━━
  'chat.kicker': 'Demande à SmartAnalyst',
  'chat.title': 'Que veux-tu savoir ?',
  'chat.placeholder': 'ex : Pourquoi le CVR Meta a chuté hier ?',
  'chat.send': 'Envoyer',
  'chat.thinking': 'Réflexion…',
  'chat.stop': 'Stop',
  'chat.stop.aria': 'Arrêter la génération',
  'chat.copy': 'Copier',
  'chat.mode.aria': 'Profondeur de réponse',
  'chat.mode.fast': 'Rapide',
  'chat.mode.deep': 'Approfondi',
  'chat.feedback.up': 'Réponse utile',
  'chat.feedback.down': 'Signaler comme inexact',
  // ━━━ Global search (cahier §3 Lot 2) ━━━
  'search.aria': 'Recherche conversations, insights et rapports',
  'search.placeholder': 'Rechercher…',
  'search.hint': 'Tape au moins 2 caractères',
  'search.loading': 'Recherche…',
  'search.loadError': 'Recherche échouée.',
  'search.noResults': 'Aucun résultat',
  'search.untitled': 'Sans titre',
  'search.section.conversations': 'Conversations',
  'search.section.insights': 'Insights',
  'search.section.reports': 'Rapports',
  'search.open.aria': 'Ouvrir la recherche (Cmd+K)',
  'chat.emptyState':
    'Pose n’importe quelle question sur ta performance marketing. Les premiers connecteurs + intégration métriques arrivent — pour l’instant SmartAnalyst répond à partir de ses connaissances marketing générales.',
  'chat.suggestions': 'Essaie',
  'chat.suggestion1': 'Comment on calcule un CAC blended ?',
  'chat.suggestion2': 'Quel ratio LTV/CAC sain pour un SaaS ?',
  'chat.suggestion3': 'Comment détecter une fatigue créative Meta ?',
  'chat.error': 'Quelque chose a planté. Réessaie.',
  'chat.error.budget':
    'Ton budget IA mensuel est atteint. Augmente-le dans Réglages ou attends le 1ᵉʳ du mois.',
  'chat.error.budget.cta': 'Passer au Pro →',
  'chat.error.rateLimit':
    "Trop de questions d'un coup — l'IA est saturée. Patiente quelques secondes et réessaie.",
  'chat.error.timeout':
    'La réponse a mis trop de temps. Réessaie, parfois la 2e passe est plus rapide.',
  'chat.error.providerDown': "L'IA est temporairement indisponible. Réessaie dans une minute.",
  'chat.error.creditDepleted':
    'Le mode Approfondi est temporairement indisponible. Repasse en mode Rapide pour continuer.',
  'chat.error.retry': 'Réessayer',
  'chat.modeDowngraded':
    "Le mode Approfondi n'est pas disponible sur ton plan — cette réponse a utilisé le mode Rapide.",
  'chat.you': 'Toi',
  'chat.assistant': 'SmartAnalyst',
  'chat.sources': 'Sources',
  'chat.attachedFile': 'fichier joint',
  'chat.removeAttachment': 'Retirer le fichier',
  'chat.new.cta': 'Nouvelle conversation',
  'chat.new.title': "Démarrer un nouveau fil — l'actuel reste sauvegardé.",
  'chat.history.cta': 'Mes conversations',
  'chat.history.loading': 'Chargement…',
  'chat.history.empty': "Aucune conversation sauvegardée pour l'instant.",
  // ━━━ Chat V2.1 — empty state hero + quick cards (cahier 22b §3.1) ━━━
  'chat.hero.title': "Comment puis-je t'aider aujourd'hui ?",
  'chat.hero.placeholder.1': 'Compare mes canaux sur 30 jours…',
  'chat.hero.placeholder.2': 'Pourquoi mon ROAS baisse cette semaine ?',
  'chat.hero.placeholder.3': 'Génère mon rapport mensuel',
  'chat.hero.placeholder.4': 'Quelles 3 actions prioriser ?',
  'chat.hero.placeholder.5': 'Quelles campagnes font 80 % du CA ?',
  // First-run state (aucun connecteur)
  'chat.empty.firstRun.eyebrow': 'Pour commencer',
  'chat.empty.firstRun.title': 'Connecte ta première source',
  'chat.empty.firstRun.body':
    "J'ai besoin d'une source connectée (GA4, Meta Ads, Google Ads, Stripe…) avant de pouvoir t'aider. Ça prend 2 minutes — reviens ensuite ici et pose-moi n'importe quelle question.",
  'chat.empty.firstRun.cta': 'Connecter une source',
  'chat.empty.firstRun.hint':
    'Pas de source = pas de données. Dès qu’un connecteur est actif, les cartes de démarrage apparaissent ici.',
  'chat.qc.compare.title': 'Comparer canaux',
  'chat.qc.compare.desc': 'Ventilation 30 j',
  'chat.qc.compare.prompt': "Compare mes canaux d'acquisition sur les 30 derniers jours.",
  'chat.qc.revenue.title': 'Évolution du CA',
  'chat.qc.revenue.desc': 'Courbe jour par jour',
  'chat.qc.revenue.prompt': "Trace l'évolution de mon chiffre d'affaires par jour cette semaine.",
  'chat.qc.report.title': 'Rapport mensuel',
  'chat.qc.report.desc': 'Modèle exécutif',
  'chat.qc.report.prompt': 'Génère mon rapport du mois en cours, template exécutif.',
  'chat.qc.funnel.title': 'Funnel e-commerce',
  'chat.qc.funnel.desc': 'Sessions → commandes',
  'chat.qc.funnel.prompt': 'Affiche le funnel conversion : sessions → ajout panier → commande.',
  'chat.qc.pareto.title': 'Pareto budget',
  'chat.qc.pareto.desc': '80/20 du ROAS',
  'chat.qc.pareto.prompt': 'Quelles 20 % de mes campagnes font 80 % du ROAS ?',
  'chat.qc.alerts.title': 'Alertes auto',
  'chat.qc.alerts.desc': 'Anomalies de la semaine',
  'chat.qc.alerts.prompt': 'Quelles métriques chutent anormalement cette semaine ?',
  'chat.qc.compare_prev.title': 'Vs. période N-1',
  'chat.qc.compare_prev.desc': 'Deltas KPIs principaux',
  'chat.qc.compare_prev.prompt': 'Compare ce mois au précédent — KPIs principaux, deltas.',
  'chat.qc.actions.title': 'Que faire',
  'chat.qc.actions.desc': '3 actions prioritaires',
  'chat.qc.actions.prompt': 'Donne-moi 3 actions concrètes prioritaires basées sur mes données.',
  // ━━━ Chat V2.1 — composer toolbar (cahier 22b §3.2) ━━━
  'chat.toolbar.attach': 'Joindre un fichier',
  'chat.toolbar.sources': 'Filtrer sources',
  'chat.toolbar.sources.allActive': 'Toutes les sources actives',
  'chat.toolbar.sources.empty': "Aucune source active — connecte un connecteur d'abord.",
  'chat.toolbar.sources.clear': 'Revenir à toutes les sources',
  'chat.toolbar.send.aria': 'Envoyer le message',
  // ━━━ Chat V2.1 — tool badge pendant streaming (cahier 22b §3.5) ━━━
  'chat.tool.running': 'Interrogation',
  'chat.tool.running.ga4': 'Lecture Google Analytics…',
  'chat.tool.running.meta_ads': 'Lecture Meta Ads…',
  'chat.tool.running.google_ads': 'Lecture Google Ads…',
  'chat.tool.running.stripe': 'Lecture Stripe…',
  'chat.tool.running.search_console': 'Lecture Search Console…',
  'chat.tool.running.get_health_score': 'Calcul du score de santé…',
  'chat.tool.running.list_top_insights': 'Top insights…',
  'chat.tool.running.list_pending_actions': 'Actions en attente…',
  'chat.tool.running.get_metric_series': 'Récupération série métrique…',
  'chat.tool.running.get_traffic_sources': 'GA4 en direct…',
  'chat.tool.running.create_action_card': "Création d'une action…",
  'chat.tool.running.create_watch': "Création d'une veille…",
  'chat.tool.running.compute_table_from_metrics': 'Construction du tableau…',
  'chat.tool.running.compare_metrics': 'Comparaison des deux sources…',
  'chat.tool.running.compute_funnel': 'Calcul du funnel…',
  'chat.tool.running.build_dashboard_preview': 'Construction du dashboard…',
  'chat.tool.running.query_ga4': 'Interrogation Google Analytics en direct…',
  'chat.tool.running.query_meta_ads': 'Interrogation Meta Ads en direct…',
  'chat.tool.running.query_stripe': 'Interrogation Stripe en direct…',
  'chat.tool.running.query_shopify': 'Interrogation Shopify en direct…',
  'chat.tool.running.query_search_console': 'Interrogation Search Console en direct…',
  'chat.tool.running.analyze': 'Verdict analyste en cours…',
  'chat.plan.title': "Plan d'analyse",
  'chat.actionPlan.badge': "Plan d'action",
  'chat.actionPlan.add': 'Ajouter à mes tâches',
  'chat.actionPlan.added': 'Ajouté à tes tâches',
  'chat.actionPlan.viewTasks': 'Voir mes tâches',
  'chat.actionPlan.failed': "Impossible d'ajouter les tâches. Réessaie.",
  'chat.actionPlan.impact': 'Impact',
  'chat.actionPlan.effort': 'Effort',
  'chat.actionPlan.prio.critical': 'Critique',
  'chat.actionPlan.prio.high': 'Haute',
  'chat.actionPlan.prio.medium': 'Moyenne',
  'chat.actionPlan.prio.low': 'Basse',
  'chat.history.rename': 'Renommer',
  'chat.history.renamePrompt': 'Nouveau titre de la conversation :',
  'chat.history.delete': 'Supprimer',
  'chat.history.deleteConfirm': 'Supprimer cette conversation ? Action irréversible.',
  // ━━━ Chat V2.1 — action shelf (cahier 22b §3.4) ━━━
  'chat.shelf.excel': 'Excel',
  'chat.shelf.excel.aria': 'Exporter la réponse en Excel',
  'chat.shelf.csv': 'CSV',
  'chat.shelf.csv.aria': 'Exporter la réponse en CSV',
  'chat.shelf.copy': 'Copier',
  'chat.shelf.copy.aria': 'Copier la réponse en markdown',
  'chat.shelf.copy.done': 'Copié',
  'chat.shelf.rerun': 'Rejouer',
  'chat.shelf.rerun.aria': 'Rejouer la même question avec le mode opposé',
  'chat.shelf.pin': 'Épingler',
  'chat.shelf.pin.aria': 'Épingler un graphique de cette réponse au dashboard',
  'chat.shelf.pin.soon': 'Arrive avec V2.3',
  'chat.shelf.report': 'Rapport',
  'chat.shelf.report.aria': 'Ouvre le générateur de rapport pré-rempli',
  'chat.shelf.slides': 'Slides',
  'chat.shelf.slides.aria': 'Générer un deck de slides (.pptx) à partir de cette réponse',
  'chat.shelf.proOnly': 'Plan Pro requis',
  // ━━━ Pin to dashboard (Lot V2.3) ━━━
  'chat.pin.success': 'Épinglé au dashboard.',
  'chat.pin.error': "Impossible d'épingler ce widget.",
  'chat.pin.max': 'Maximum de widgets épinglés atteint — retire-en un avant.',
  'chatWidget.open': 'Ouvrir l’assistant IA',
  'chatWidget.close': 'Fermer',
  'chatWidget.title': 'Demande à SmartAnalyst',
  'chatWidget.greeting': 'Pose-moi une question sur ton marketing, tes données, ou ton audit.',
  'nav.reports': 'Rapports',
  'nav.files': 'Fichiers',
  'nav.settings': 'Paramètres',
  'nav.help': 'Aide',
  'nav.soon': 'bientôt',

  // ━━━ Locale switcher ━━━
  'locale.label': 'Langue',
  'locale.english': 'English',
  'locale.french': 'Français',

  // ━━━ Login ━━━
  'login.welcomeBack': 'Bon retour.',
  'login.subtitle': 'Connecte-toi à ton espace SmartAnalyst.',
  'login.emailPlaceholder': 'toi@entreprise.com',
  'login.passwordPlaceholder': '••••••••••••',
  'login.submit': 'Se connecter',
  'login.submitting': 'Connexion…',
  'login.somethingWentWrong': 'Une erreur est survenue',
  'login.newToSmartAnalyst': 'Nouveau sur SmartAnalyst ?',
  'login.createAccount': 'Créer un compte',
  'login.googleButton': 'Continuer avec Google',
  'login.googleRedirecting': 'Redirection…',
  'login.googleStartError': 'Impossible de démarrer la connexion Google',
  'login.forgotPassword': 'Mot de passe oublié ?',

  // ━━━ Password reset ━━━
  'reset.requestTitle': 'Réinitialise ton mot de passe.',
  'reset.requestSubtitle':
    "Saisis l'email lié à ton compte SmartAnalyst. On t'envoie un lien de réinitialisation.",
  'reset.requestSubmit': 'Envoyer le lien',
  'reset.requestSubmitting': 'Envoi…',
  'reset.requestDoneTitle': 'Regarde tes mails.',
  'reset.requestDoneBody':
    'Si un compte existe pour {{email}}, un lien est en route. Il expire dans 1 heure.',
  'reset.confirmTitle': 'Choisis un nouveau mot de passe.',
  'reset.confirmSubtitle': 'Choisis quelque chose de mémorable. Tu seras connecté juste après.',
  'reset.newPassword': 'Nouveau mot de passe',
  'reset.confirmPassword': 'Confirme le nouveau mot de passe',
  'reset.confirmSubmit': 'Mettre à jour',
  'reset.confirmSubmitting': 'Mise à jour…',
  'reset.backToLogin': 'Retour à la connexion',
  'reset.mismatch': 'Les mots de passe ne correspondent pas.',
  'reset.tooShort': 'Le mot de passe doit faire au moins 12 caractères.',
  'reset.linkExpired': 'Ce lien est invalide ou a expiré. Demande-en un nouveau.',
  'reset.error': 'Quelque chose a planté. Réessaie.',

  // ━━━ Signup ━━━
  'signup.title': 'Crée ton espace de travail.',
  'signup.subtitle': 'Prêt en moins d’une minute.',
  'signup.organizationName': 'Nom de ton organisation',
  'signup.organizationPlaceholder': 'Acme SAS',
  'signup.workEmail': 'Email pro',
  'signup.passwordPlaceholder': 'Au moins 12 caractères',
  'signup.passwordHint': '12 caractères minimum. Une passphrase facile à retenir, c’est parfait.',
  'signup.submit': 'Créer le compte',
  'signup.submitting': 'Création…',
  'signup.alreadyHaveAccount': 'Déjà un compte ?',
  'signup.signIn': 'Se connecter',
  'signup.googleButton': 'S’inscrire avec Google',

  // ━━━ Auth callback ━━━
  'callback.signingIn': 'Connexion en cours…',
  'callback.oneSec': 'Une seconde.',
  'callback.failedTitle': 'Connexion Google échouée',
  'callback.backToLogin': 'Retour à la connexion',
  'callback.err.accessDenied': 'Tu as refusé l’accès Google.',
  'callback.err.notConfigured': 'La connexion Google n’est pas encore activée côté serveur.',
  'callback.err.codeExchange': 'Google a refusé le code d’autorisation. Réessaie.',
  'callback.err.supabase':
    'Supabase a refusé la session Google. Vérifie que le provider est activé.',
  'callback.err.stateInvalid': 'Le lien a expiré. Réessaie depuis la page de connexion.',
  'callback.err.stateMissing': 'Lien invalide. Réessaie.',
  'callback.err.missingCodeOrState': 'Réponse Google invalide. Réessaie.',
  'callback.err.missingResponse': 'Réponse manquante.',
  'callback.err.incompleteResponse': 'Réponse incomplète. Réessaie.',
  'callback.err.malformed': 'Réponse mal formée.',
  'callback.err.generic': 'Erreur Google : {{code}}',

  // ━━━ Beta lockdown ━━━
  'betaLocked.badge': 'Beta privée',
  'betaLocked.title': 'SmartAnalyst est en beta privée.',
  'betaLocked.body':
    "On finalise l'app pour quelques marketers triés sur le volet. Rejoins la waitlist et on te recontacte avec un accès dès qu'on est prêt.",
  'betaLocked.cta': 'Rejoindre la waitlist',
  'betaLocked.alreadyInvited': 'Déjà invité ?',
  'betaLocked.backToLogin': 'Retour à la connexion',

  // ━━━ Dashboard ━━━
  'dashboard.overview': 'Vue d’ensemble',
  'dashboard.greetingNamed': 'Hello {{name}}, voici ce qui a changé.',
  'dashboard.greeting': 'Hello, voici ce qui a changé.',
  'dashboard.window.7d': '7j',
  'dashboard.window.30d': '30j',
  'dashboard.window.90d': '90j',
  'dashboard.emptyConnectorsTitle': 'Connecte une source pour démarrer.',
  'dashboard.emptyConnectorsBody':
    'Branche GA4, Meta Ads ou Stripe en 2 clics. On commence à synchroniser les 90 derniers jours pour remplir ton tableau de bord automatiquement.',
  'dashboard.addConnector': 'Ajouter un connecteur →',
  'dashboard.couldNotLoad': 'Impossible de charger les métriques. {{message}}',
  'dashboard.tile.syncing': 'Synchronisation…',
  'dashboard.tile.noData': 'Pas encore de données',
  'dashboard.tile.noBaseline': 'vs précédent : pas de base de comparaison',
  'dashboard.tile.vsPrevious': 'vs période précédente',
  'dashboard.next.01.title': 'Connecte plus de sources',
  'dashboard.next.01.body':
    'Plus tu connectes de données, plus les insights cross-sources de l’IA deviennent précis.',
  'dashboard.next.01.cta': 'Ouvrir les connecteurs',
  'dashboard.next.02.title': 'Pose ta première question',
  'dashboard.next.02.body':
    'Essaie “Qu’est-ce qui a changé cette semaine ?” ou “Pourquoi le taux de conversion a chuté hier ?”.',
  'dashboard.next.02.cta': 'Ouvrir le chat',
  'dashboard.next.03.title': 'Planifie un rapport hebdo',
  'dashboard.next.03.body':
    'PDF généré automatiquement, envoyé chaque lundi avec un résumé exécutif.',
  'dashboard.next.03.cta': 'Configurer',
  'dashboard.tile.sessions': 'Sessions',
  'dashboard.tile.conversions': 'Conversions',
  'dashboard.tile.revenue': 'Revenus',
  'dashboard.tile.adSpend': 'Dépenses pub',
  'dashboard.tile.mrr': 'MRR',
  'dashboard.tile.activeCustomers': 'Clients actifs',
  'dashboard.tile.newCustomers': 'Nouveaux clients',
  'dashboard.tile.failedPayments': 'Paiements échoués',
  'dashboard.tile.activeUsers': 'Utilisateurs actifs',
  'dashboard.tile.bounceRate': 'Taux de rebond',
  'dashboard.tile.metaSpend': 'Dépenses Meta',
  'dashboard.tile.metaClicks': 'Clics Meta',
  'dashboard.tile.metaConversions': 'Conversions Meta',
  'dashboard.tile.metaRoas': 'ROAS Meta',
  'dashboard.tile.googleAdsSpend': 'Dépenses Google Ads',
  'dashboard.tile.googleAdsClicks': 'Clics Google Ads',
  'dashboard.tile.googleAdsConversions': 'Conv. Google Ads',
  'dashboard.tile.googleAdsCtr': 'CTR Google Ads',
  'dashboard.tile.shopifyRevenue': 'Revenus Shopify',
  'dashboard.tile.orders': 'Commandes',
  'dashboard.tile.aov': 'Panier moyen',
  'dashboard.tile.organicClicks': 'Clics organiques',
  'dashboard.tile.organicImpressions': 'Impressions organiques',
  'dashboard.tile.organicCtr': 'CTR organique',
  'dashboard.tile.avgPosition': 'Position moyenne',

  // ━━━ Home: Tracking Health ━━━
  'home.health.kicker': 'Santé du tracking',
  'home.health.tagActive': 'SmartTag actif',
  'home.health.tagInactive': 'SmartTag pas installé',
  'home.health.activeBody':
    'Dernier événement il y a {{ago}} · {{events7}} événements sur les 7 derniers jours.',
  'home.health.inactiveBody':
    'Installe le SmartTag pour comparer ce que tu observes vs ce que GA4 / Meta / Stripe te disent.',
  'home.health.installCta': 'Installer SmartTag',
  'home.health.events7d': 'Events 7j',
  'home.health.events30d': 'Events 30j',
  'home.health.sources': 'Sources connectées',
  'home.health.confidence.high': 'Confiance élevée',
  'home.health.confidence.medium': 'Confiance moyenne',
  'home.health.confidence.low': 'Confiance faible',

  // ━━━ Home: Brief du jour (Insights + Actions) ━━━
  'home.brief.kicker': 'Brief du jour',
  'home.brief.title': 'Ce qui mérite ton attention.',
  'home.brief.count': '{{count}} ouverts',
  'home.brief.empty.body':
    "Pas encore d'insights. Connecte tes sources et Smart Analyst remontera ce qui compte vraiment.",
  'home.brief.empty.cta': 'Ouvrir les connecteurs',
  'home.brief.evidence': 'Autres preuves',
  'home.brief.actions': 'Actions recommandées',
  'home.brief.limitations': 'Limites',
  'home.brief.markResolved': 'Marquer résolu',
  'home.brief.snooze': 'Reporter',
  'home.brief.dismiss': 'Ignorer',
  'home.brief.impact': 'Impact',
  'home.brief.effort': 'Effort',
  'home.brief.priority': 'Priorité',
  'home.brief.task.validate': 'Valider',
  'home.brief.task.archive': 'Écarter',

  // ━━━ Page Tâches ━━━
  'tasks.kicker': 'Tâches',
  'tasks.title': 'Ce qu’il y a à faire.',
  'tasks.subtitle': 'Cure ce que l’IA propose, traite ce que tu as validé, archive le reste.',
  'tasks.loadError': 'Impossible de charger les tâches.',
  'tasks.col.inbox': 'À curer',
  // ━━━ NotificationCenter (cahier §3 Lot 1) ━━━
  'notif.title': 'Notifications',
  'notif.markAll': 'Tout marquer lu',
  'notif.loading': 'Chargement…',
  'notif.loadError': 'Impossible de charger les notifications.',
  'notif.empty.title': 'Rien de neuf',
  'notif.empty.body': "On t'alerte dès que quelque chose bouge.",
  'notif.aria.bell': 'Notifications',
  'notif.aria.unread': '{{count}} notifications non lues',
  'notif.aria.panel': 'Panneau de notifications',
  // ━━━ Design-system d'états (cahier §3 Lot 2 + §4.9) ━━━
  'state.error.title': 'Quelque chose a coincé',
  'state.error.body': "On n'a pas pu charger ça. Réessaie — si ça persiste, contacte le support.",
  'state.error.retry': 'Réessayer',
  'state.permission.title': 'Pas inclus dans ton plan',
  'state.permission.body':
    "Passe au plan supérieur pour débloquer, ou demande à l'admin du workspace.",
  'state.permission.upgrade': 'Voir les plans',
  // ━━━ DataFreshness (cahier §3 Lot 2 + §4.2) ━━━
  'freshness.loading': 'Vérification…',
  'freshness.none': 'Aucune source connectée',
  'freshness.upToDate': 'À jour',
  'freshness.lastSync': 'Dernière sync {{rel}}',
  'freshness.needsHelp': 'Une source a besoin d’aide',
  'tasks.col.today': 'Aujourd’hui',
  'tasks.col.done': 'Faites',
  'tasks.empty.inbox': 'Aucune nouvelle suggestion.',
  'tasks.empty.today': 'Rien dans ta liste.',
  'tasks.empty.done': 'Rien de fait pour l’instant.',
  'tasks.emptyAll.body': 'Pas encore de tâches. L’IA les fera remonter en analysant tes données.',
  'tasks.emptyAll.cta': 'Connecter une source',
  'tasks.action.validate': 'Valider',
  'tasks.action.archive': 'Archiver',
  'tasks.action.markDone': 'Marquer faite',
  'tasks.action.reopen': 'Rouvrir',
  'tasks.action.emailBrief': 'Envoyer en brief',
  'tasks.emailModal.title': 'Envoyer comme brief',
  'tasks.emailModal.recipient': 'Email du destinataire',
  'tasks.emailModal.note': 'Note perso (optionnel)',
  'tasks.emailModal.notePlaceholder': 'Un mot pour le destinataire.',
  'tasks.emailModal.cancel': 'Annuler',
  'tasks.emailModal.send': 'Envoyer',
  'tasks.emailModal.sending': 'Envoi…',
  'tasks.emailModal.sent': 'Envoyé ✓',

  // ━━━ Onboarding checklist ━━━
  'onboarding.kicker': 'Premiers pas',
  'onboarding.title': 'Configure SmartAnalyst en 3 étapes.',
  'onboarding.dismiss': 'Masquer',
  'onboarding.step.connect.title': 'Connecte une source de données',
  'onboarding.step.connect.body':
    'GA4, Meta Ads, Stripe… en 2 clics. On synchronise les 30 derniers jours.',
  'onboarding.step.connect.cta': 'Connecter',
  'onboarding.step.tag.title': 'Installe le SmartTag (optionnel)',
  'onboarding.step.tag.body': 'Ta vérité terrain, pour recouper ce que GA4 / Meta racontent.',
  'onboarding.step.tag.cta': 'Installer',
  'onboarding.step.ask.title': 'Pose ta première question',
  'onboarding.step.ask.body':
    'Essaie « Qu’est-ce qui a changé cette semaine ? » — causes, preuves, actions.',
  'onboarding.step.ask.cta': 'Ouvrir le chat',

  // ━━━ Connectors ━━━
  'connectors.kicker': 'Intégrations',
  'connectors.title': 'Connecte tes sources de données.',
  'connectors.subtitle':
    '{{available}} disponibles · {{soon}} dans la roadmap. La tienne n’y est pas ?',
  'connectors.tellUs': 'Dis-le nous',
  'connectors.searchPlaceholder': 'Rechercher une intégration…',
  'connectors.category.all': 'Toutes',
  'connectors.category.Analytics': 'Analytics',
  'connectors.category.Advertising': 'Publicité',
  'connectors.category.Payments': 'Paiements',
  'connectors.category.CRM': 'CRM',
  'connectors.category.Email & SMS': 'Email & SMS',
  'connectors.category.Ecommerce': 'E-commerce',
  'connectors.category.Product & Events': 'Produit & Événements',
  'connectors.category.SEO': 'SEO',
  'connectors.category.Social': 'Social',
  'connectors.category.Support': 'Support',
  'connectors.category.Data warehouse': 'Data warehouse',
  'connectors.category.Spreadsheet & Files': 'Tableurs & Fichiers',
  'connectors.section.available': 'Disponibles',
  'connectors.section.soon': 'Bientôt',
  'connectors.emptyResults': 'Aucune intégration ne correspond à ta recherche.',
  'connectors.badge.connected': 'Connecté',
  'connectors.badge.soon': 'Bientôt',
  'connectors.auth.oauth': 'OAuth',
  'connectors.auth.apiKey': 'Clé API',
  'connectors.action.connect': 'Connecter',
  'connectors.action.opening': 'Ouverture…',
  'connectors.action.cancel': 'Annuler',
  'connectors.action.disconnect': 'Déconnecter',
  'connectors.action.disconnecting': 'Déconnexion…',
  'connectors.action.notifyMe': 'Me prévenir',
  'connectors.action.sync': 'Synchroniser',
  'connectors.action.reconnect': 'Reconnecter',
  'sources.file.deleteConfirm': 'Supprimer ce fichier ? Action irréversible.',
  'connectors.action.syncing': 'Synchronisation…',
  'connectors.confirmDisconnect': 'Déconnecter {{name}} ?',
  'connectors.err.startOauth': 'Impossible de démarrer le flow OAuth',
  'connectors.err.disconnect': 'Impossible de déconnecter',
  'connectors.err.sync': 'Synchronisation impossible',
  'connectors.sync.success': '{{count}} métriques synchronisées',
  'connectors.apikey.label': 'Colle ta clé API {{name}}',
  'connectors.apikey.save': 'Connecter',
  'connectors.apikey.saving': 'Connexion…',
  'connectors.loading': 'Chargement du catalogue…',
  'connectors.badge.notConfigured': 'App OAuth pas encore configurée côté SmartAnalyst.',
  'connectors.subdomain.label': 'Sous-domaine de ta boutique Shopify',

  // ━━━ Live (SmartTag temps-réel) ━━━
  'live.kicker': 'Smart tag',
  'live.title': 'Ce qui se passe sur ton site, maintenant.',
  'live.subtitle':
    'Événements streamés en direct par ton tag SmartAnalyst. Pas de stockage en formule gratuite — un refresh remet à zéro.',
  'live.stat.activeSessions': 'Sessions actives',
  'live.stat.pageviews': 'Pages vues',
  'live.stat.clicks': 'Clics',
  'live.stat.errors': 'Erreurs',
  'live.recentEvents': 'Événements récents',
  'live.windowHint': 'Sur les 5 dernières minutes',
  'live.empty': 'Aucun événement — installe le tag sur ton site pour démarrer le stream.',
  'live.connecting': 'Connexion au flux…',
  'live.conn.connecting': 'Connexion',
  'live.conn.connected': 'En direct',
  'live.conn.disconnected': 'Déconnecté',
  'live.conn.error': 'Erreur de connexion',
  'live.conn.unauthorized': 'Non autorisé',
  'live.err.unauthorized': "Ta session n'a pas accès au flux temps-réel de cet espace.",
  'live.eventType.pageview': 'vue',
  'live.eventType.click': 'clic',
  'live.eventType.error': 'erreur',
  'live.eventType.session_start': 'session',
  'live.eventType.custom': 'événement',
  'live.install.title': 'Installe ton SmartTag',
  'live.install.subtitle':
    'Colle ce snippet dans le <head> de ton site pour recevoir des événements ici.',
  'live.install.cta.guide': 'Guide d’installation complet',
  'live.install.cta.scroll': 'Voir les events ↓',
  'live.install.cta.audit': 'Auditer mon site',

  // ━━━ Smart tag onboarding (aucun event reçu) ━━━
  'live.onboarding.kicker': 'Setup du Smart tag',
  'live.onboarding.title': 'Active ton Smart tag en 3 minutes',
  'live.onboarding.subtitle':
    'Un snippet à coller dans ton site. Analytics temps réel, audits SEO/GEO à la demande, zéro cookie.',
  'live.onboarding.why.title': 'Ce que le Smart tag te donne',
  'live.onboarding.why.analytics.title': 'Analytics temps réel',
  'live.onboarding.why.analytics.body':
    'Pages vues, clics, erreurs en direct — vois ton trafic au moment où il se passe, pas le lendemain.',
  'live.onboarding.why.audit.title': 'Audit SEO + GEO à la demande',
  'live.onboarding.why.audit.body':
    'Lance un audit 4 axes (SEO, recherche IA, perf, citabilité) sur n’importe quelle page en 30 secondes.',
  'live.onboarding.why.privacy.title': 'Sans cookie, RGPD-friendly',
  'live.onboarding.why.privacy.body':
    'Pas de bandeau cookies. ID de session en sessionStorage, effacé à la fermeture de l’onglet. Compatible CNIL.',
  'live.onboarding.step1.title': 'Copie ton snippet',
  'live.onboarding.step1.body':
    'Il est déjà rempli avec la clé d’écriture de ton espace. Tu n’as qu’à le copier.',
  'live.onboarding.step2.title': 'Colle-le dans le <head>',
  'live.onboarding.step2.body':
    'Sur chaque page à tracker (ou dans ton template / layout partagé). WordPress, Shopify, Webflow, Ghost — voir le guide pour les étapes exactes.',
  'live.onboarding.step2.cta': 'Guide d’installation par CMS',
  'live.onboarding.step3.title': 'Vérifie que ça marche',
  'live.onboarding.step3.body':
    'Une fois installé, ouvre ton site dans un autre onglet et navigue. Reviens ici et clique « Tester » — on détecte le 1er event en direct.',
  'live.onboarding.step3.button': 'Tester l’installation',
  'live.onboarding.step3.checking': 'Écoute des événements en cours… (jusqu’à 30 secondes)',
  'live.onboarding.step3.success': 'Tag détecté — redirection vers ton dashboard',
  'live.onboarding.step3.timeout':
    'Aucun event reçu après 30 secondes. Vérifie que le snippet est bien dans ton <head> et que tu as bien ouvert ton site dans un autre onglet.',
  'live.onboarding.step3.retry': 'Réessayer',

  // ━━━ Settings ━━━
  'settings.kicker': 'Paramètres',
  'settings.title': 'Compte & espace.',
  'settings.section.profile': 'Profil',
  'settings.section.workspace': 'Espace',
  'settings.section.security': 'Sécurité',
  'settings.section.session': 'Session',
  'settings.section.language': 'Langue',
  'settings.section.notifications': 'Notifications',
  'settings.section.aiUsage': 'Usage IA',
  'settings.section.data': 'Tes données',
  // ━━━ Quota chip + toast ━━━
  'quota.chip.connectors': 'sources',
  'quota.chip.insights': 'insights',
  'quota.chip.normalHint': 'Ton quota mensuel — clique pour gérer ton plan',
  'quota.chip.criticalHint': 'Tu approches de ta limite mensuelle',
  'quota.chip.exceeded': 'Limite mensuelle atteinte',
  'quota.toast.insights.warn.title': 'Tu approches de ton quota insights',
  'quota.toast.insights.exceeded.title': 'Quota insights mensuel atteint',
  'quota.toast.connectors.warn.title': 'Presque à la limite de sources',
  'quota.toast.connectors.exceeded.title': 'Limite de sources atteinte',
  'quota.chip.aiTokens': 'tokens IA',
  'quota.toast.aiTokens.warn.title': 'Tu approches de ta limite IA',
  'quota.toast.aiTokens.exceeded.title': 'Limite IA mensuelle atteinte',
  'quota.toast.cta': 'Passer au Pro',
  'aiUsage.title': 'Tokens consommés ce mois',
  'aiUsage.subtitle': 'Chat, insights, validation alertes…',
  'aiUsage.tokensLabel': 'tokens',
  'aiUsage.budget': 'Budget mensuel',
  'aiUsage.noBudget': 'Aucune limite mensuelle définie. Contacte-nous pour en fixer une.',
  'aiUsage.warningHigh': 'Tu approches de ta limite mensuelle.',
  'aiUsage.breakdown': 'Répartition par fonctionnalité',
  'aiUsage.calls': 'appels',
  'notifSettings.weeklyDigest.title': 'Résumé hebdomadaire',
  'notifSettings.weeklyDigest.body': 'Un mail court le lundi matin avec les insights à regarder.',
  'notifSettings.criticalAlerts.title': 'Alertes critiques',
  'notifSettings.criticalAlerts.body': 'Un mail immédiat dès qu’un insight critique est détecté.',
  'notifSettings.emailOverride.title': 'Email personnalisé',
  'notifSettings.emailOverride.body': 'Remplace l’email par défaut. Vide = email du compte.',
  'notifSettings.emailOverride.placeholder': 'toi@entreprise.fr',
  'notifSettings.save': 'Enregistrer',
  'notifSettings.saved': 'Enregistré ✓',
  'settings.section.dangerZone': 'Zone dangereuse',
  'settings.field.fullName': 'Nom complet',
  'settings.field.userId': 'ID utilisateur',
  'settings.field.workspaceName': 'Nom',
  'settings.field.workspaceId': 'ID de l’espace',
  'settings.field.yourRole': 'Ton rôle',
  'settings.password.title': 'Mot de passe',
  'settings.password.body': 'Réinitialise ton mot de passe par email. On t’envoie un lien magique.',
  'settings.password.button': 'Réinitialiser le mot de passe',
  'settings.signOut.title': 'Se déconnecter partout',
  'settings.signOut.body': 'Termine cette session et invalide le refresh token.',
  'settings.export.title': 'Télécharger tes données',
  'settings.export.body':
    'Récupère un export JSON de tout ce qu’on a sur toi (workspaces, connecteurs, métriques, audit logs).',
  'settings.export.button': 'Télécharger',
  'settings.export.busy': 'Préparation…',
  'settings.export.success': 'Export téléchargé',
  'settings.export.empty':
    "Export téléchargé — ton workspace n'a pas encore de connecteur ni de métrique, le fichier est presque vide.",
  'settings.delete.title': 'Supprimer mon compte',
  'settings.delete.body':
    'Supprime définitivement ton compte et les workspaces dont tu es seul·e membre. Les audit logs sont conservés anonymisés (rétention légale). Action irréversible.',
  'settings.delete.confirmHint': 'Saisis DELETE MY ACCOUNT pour activer le bouton.',
  'settings.delete.button': 'Supprimer le compte',
  'settings.delete.busy': 'Suppression…',
  'settings.language.body':
    'Change la langue de l’interface. Ton choix est gardé sur cet appareil.',
  'settings.section.tracking': 'Tracking — SmartTag',
  'settings.section.billing': 'Plan & facturation',
  'settings.section.whiteLabel': 'Branding',
  'settings.section.apiKeys': 'Clés API',
  // ━━━ White-label (cahier §3 Lot 4) ━━━
  'whiteLabel.title': 'Couleurs de marque et logo',
  'whiteLabel.body':
    'Personnalise le rendu des rapports générés et de ton workspace. Appliqué sur chaque export.',
  'whiteLabel.color': 'Couleur principale',
  'whiteLabel.color.invalid': 'Format #RRGGBB attendu',
  'whiteLabel.logo': 'URL du logo',
  'whiteLabel.logo.invalid': 'Doit être une URL https://',
  'whiteLabel.save': 'Enregistrer',
  'whiteLabel.saving': 'Sauvegarde…',
  'whiteLabel.saved': 'Sauvegardé',
  'whiteLabel.error': 'Échec de la sauvegarde',
  // ━━━ Clés API (cahier §3 Lot 4) ━━━
  'apiKeys.title': 'Clés API',
  'apiKeys.body':
    "Génère des clés pour scripter l'accès aux données de ton workspace (export, automatisations, intégrations custom).",
  'apiKeys.create': 'Générer une clé',
  'apiKeys.create.nameLabel': 'Nom de la clé',
  'apiKeys.create.namePlaceholder': 'ex. Zapier prod, script export mensuel',
  'apiKeys.creating': 'Génération…',
  'apiKeys.confirm': 'Générer',
  'apiKeys.cancel': 'Annuler',
  'apiKeys.error': 'Échec de la génération',
  'apiKeys.empty': 'Pas encore de clé API. Génères-en une pour commencer.',
  'apiKeys.lastUsed': 'Dernière utilisation',
  'apiKeys.revoked': 'Révoquée',
  'apiKeys.revoke': 'Révoquer',
  'apiKeys.revoke.confirm':
    'Révoquer cette clé ? Tout script qui l’utilise encore arrêtera de fonctionner immédiatement.',
  'apiKeys.revealed.warning': '⚠ Copie cette clé maintenant — elle ne sera plus jamais affichée.',
  'apiKeys.revealed.body':
    "Stocke-la dans un gestionnaire de mot de passe ou une variable d'environnement. On ne garde qu'un hash, on ne peut pas la re-montrer.",
  'apiKeys.revealed.dismiss': "Je l'ai copiée, masquer",
  'apiKeys.footer':
    "Format : sa_test_…/sa_live_… · 32 caractères base62. À passer dans l'header 'Authorization: Bearer <clé>'.",
  // ━━━ Veille snooze (cahier §3 Lot 4) ━━━
  'watch.snooze.button': 'Mettre en pause',
  'watch.snooze.24h': 'Pendant 24 h',
  'watch.snooze.7d': 'Pendant 7 jours',
  'watch.snooze.cancel': 'Réactiver maintenant',
  'watch.snoozed.until': 'En pause jusqu’au {{date}}',
  // ━━━ Team management (cahier §3 Lot 4) ━━━
  'settings.section.team': 'Équipe',
  'team.title': 'Membres et invitations',
  'team.body':
    "Invite tes collègues dans ce workspace. Les admins gèrent l'abonnement et les membres ; les éditeurs connectent des sources et agissent sur les insights ; les lecteurs sont en lecture seule.",
  'team.invite.cta': 'Inviter un membre',
  'team.invite.email': 'Adresse email',
  'team.invite.role': 'Rôle',
  'team.invite.send': "Envoyer l'invitation",
  'team.invite.sending': 'Envoi…',
  'team.invite.success': 'Invitation envoyée.',
  'team.invite.successNoEmail':
    "Invitation créée — mais l'email n'a pas pu être envoyé. Récupère le lien d'acceptation depuis ce panneau plus tard.",
  'team.invite.error': "Impossible d'envoyer l'invitation.",
  'team.invite.cancel': 'Annuler',
  'team.members.title': 'Membres actifs',
  'team.members.empty': 'Tu es le seul membre.',
  'team.members.you': 'toi',
  'team.members.remove': 'Retirer',
  'team.members.remove.confirm': 'Retirer ce membre du workspace ?',
  'team.invitations.title': 'Invitations en attente',
  'team.invitations.empty': 'Aucune invitation en attente.',
  'team.invitations.expires': 'Expire le {{date}}',
  'team.invitations.revoke': 'Révoquer',
  'team.role.admin': 'Admin',
  'team.role.editor': 'Éditeur',
  'team.role.viewer': 'Lecteur',
  'team.role.change': 'Changer de rôle',
  'team.error.lastAdmin': 'Impossible de retirer le dernier administrateur.',
  'team.error.selfRemove': 'Tu ne peux pas te retirer toi-même de ce workspace.',
  'team.error.alreadyMember': 'Cet email est déjà membre du workspace.',
  'team.error.invitePending': 'Une invitation est déjà en attente pour cet email.',
  // ━━━ Page accept invitation ━━━
  'invite.accept.title': "Accepter l'invitation",
  'invite.accept.body': 'Tu as été invité à rejoindre un workspace SmartAnalyst.',
  'invite.accept.cta': 'Accepter et continuer',
  'invite.accept.busy': 'Acceptation…',
  'invite.accept.success': 'Bienvenue ! Redirection…',
  'invite.accept.error.generic': "Impossible d'accepter cette invitation.",
  'invite.accept.error.expired': 'Cette invitation a expiré. Demande-en une nouvelle.',
  'invite.accept.error.notPending': 'Cette invitation a déjà été utilisée ou révoquée.',
  'invite.accept.error.emailMismatch':
    'Cette invitation a été envoyée à une autre adresse email. Connecte-toi avec ce compte.',
  'invite.accept.signinFirst': 'Tu dois te connecter pour accepter cette invitation.',
  // ━━━ Command palette enrichie (Lot 4) ━━━
  'palette.section.go': 'Aller à',
  'palette.section.actions': 'Actions rapides',
  'palette.go.chat': 'Ouvrir le chat',
  'palette.go.audit': 'Tableau d’audit',
  'palette.go.veille': 'Veille',
  'palette.go.tasks': 'Tâches',
  'palette.go.reports': 'Rapports',
  'palette.go.sources': 'Sources',
  'palette.go.live': 'Activité en direct',
  'palette.go.settings': 'Paramètres',
  'palette.action.newWatch': 'Créer une veille',
  'palette.action.newReport': 'Générer un rapport',
  'palette.action.openBilling': 'Ouvrir la facturation',
  'palette.action.inviteMember': 'Inviter un membre',
  // ━━━ Rapports — mot de l'analyste + templates (Lot 4) ━━━
  'reports.gen.template': 'Modèle',
  'reports.gen.template.standard': 'Standard — vue d’ensemble complète',
  'reports.gen.template.executive': 'Exécutif — score, top alertes et KPIs',
  'reports.gen.template.detail': 'Détail — KPIs par source + comparaison',
  'reports.gen.template.agency': 'Agence — white-label complet, couleurs marque',
  'reports.gen.aiNote': "Générer un mot de l'analyste (IA)",
  'reports.gen.aiNote.hint':
    'Ajoute un commentaire de 3 lignes en haut du rapport (« le mot du mois »).',
  'reports.analystNote.heading': "Le mot de l'analyste",
  // ━━━ Rapports — wizard (V2 — sources / période / contexte) ━━━
  'reports.wizard.step1': 'Sources',
  'reports.wizard.step2': 'Période',
  'reports.wizard.step3': 'Contexte',
  'reports.wizard.sourcesTitle': 'Quelles sources le rapport doit-il couvrir ?',
  'reports.wizard.sourcesAll':
    'Toutes les sources sont incluses par défaut — décoche ce que tu ne veux pas.',
  'reports.wizard.sourcesEmpty': 'Connecte d’abord une source de données pour générer un rapport.',
  'reports.wizard.sourcesEmptyCta': 'Connecter une source',
  'reports.wizard.periodTitle': 'Sur quelle période ?',
  'reports.wizard.preset.thisMonth': 'Mois en cours',
  'reports.wizard.preset.lastMonth': 'Mois dernier',
  'reports.wizard.preset.last30': '30 derniers jours',
  'reports.wizard.preset.thisQuarter': 'Trimestre en cours',
  'reports.wizard.preset.custom': 'Personnalisé',
  'reports.wizard.periodInvalid': 'La date de début doit précéder la date de fin.',
  'reports.wizard.contextTitle': 'Un contexte à donner à l’analyse ? (optionnel)',
  'reports.wizard.contextPlaceholder':
    'ex : « Promo -20 % du 10 au 15 » · « Focus SEO » · « Nouveau site lancé mi-mois »',
  'reports.wizard.contextHint':
    'L’IA intègre ce contexte dans sa lecture des chiffres et ses recommandations.',
  'reports.wizard.modeLabel': 'Analyse',
  'reports.wizard.next': 'Suivant',
  'reports.wizard.back': 'Retour',
  // ━━━ Billing (cahier §3 Lot 3) ━━━
  'billing.currentPlan': 'Plan actuel',
  'billing.active': 'Actif',
  'billing.free.description':
    'Tu es sur le plan Découverte : 1 source, 3 insights/mois. Passe Pro pour tout débloquer.',
  'billing.pro.description':
    'Plan Pro : sources illimitées, insights illimités, rapports et mode Approfondi.',
  'billing.upgradeToPro': 'Passer au plan Pro',
  'billing.upgradeToStarter': 'Ou commencer avec Starter',
  'billing.managePortal': 'Gérer mon abonnement',
  'billing.loading': 'Ouverture…',
  'billing.usage': 'Utilisation ce mois-ci',
  'billing.usage.connectors': 'Sources connectées',
  'billing.usage.insights': 'Insights générés',
  'billing.usage.reports': 'Génération de rapports',
  'billing.usage.deepChat': 'Mode Approfondi du chat',
  'billing.unlimited': '∞',
  'billing.loadError': 'Impossible de charger tes infos de facturation.',
  'billing.error.notConfigured': "La facturation n'est pas encore configurée. Contacte le support.",
  'billing.error.noCustomer': "Pas encore d'abonnement à gérer — passe Pro d'abord.",
  'billing.error.planUnavailable': "Ce plan n'est pas disponible pour le moment.",
  'billing.error.generic': 'Requête de facturation échouée. Réessaie.',
  'billing.grace.title': 'Échec de paiement',
  'billing.grace.body':
    "Ton dernier paiement n'a pas abouti. Mets à jour ton moyen de paiement dans les {days} jours pour conserver ton plan actuel.",
  'billing.grace.expiredTitle': 'Plan suspendu',
  'billing.grace.expiredBody':
    "Ton moyen de paiement n'a pas pu être débité et la période de grâce est terminée. Ton plan a été rétrogradé en Free. Mets à jour ton moyen de paiement pour réactiver.",
  'billing.grace.cta': 'Mettre à jour le paiement',
  // ━━━ UpgradePrompt ━━━
  'upgrade.title': 'Fonctionnalité Pro',
  'upgrade.cta': 'Passer au plan Pro',
  'upgrade.loading': 'Ouverture…',
  'upgrade.error': "Impossible d'ouvrir le checkout. Réessaie.",
  'upgrade.reason.connectors':
    "Tu as atteint la limite d'1 source. Le plan Pro débloque les sources illimitées.",
  'upgrade.reason.insights':
    'Tu as atteint tes 3 insights du mois. Le plan Pro lève la limite — tu ne rates plus aucun signal.',
  'upgrade.reason.reports': 'La génération de rapports est incluse dans le plan Pro.',
  'upgrade.reason.deepChat':
    'Le mode Approfondi (analyse plus longue et plus profonde) est inclus dans le plan Pro. Le mode Rapide reste accessible.',
  'upgrade.reason.generic': 'Cette fonctionnalité est dans le plan Pro.',
  'settings.section.privacy': 'Confidentialité',
  'settings.privacy.title': 'Préférences cookies',
  'settings.privacy.body':
    'Revoir ou modifier ton choix concernant les cookies analytics et marketing. Les cookies strictement nécessaires ne peuvent pas être désactivés.',
  'settings.privacy.button': 'Gérer les cookies',

  // ━━━ Bannière de consentement ━━━
  'consent.title': 'Cookies & tracking',
  'consent.message':
    'On utilise des cookies pour te garder connecté et améliorer SmartAnalyst. Analytics et marketing sont optionnels — tu peux changer d’avis à tout moment.',
  'consent.acceptAll': 'Tout accepter',
  'consent.rejectAll': 'Tout refuser',
  'consent.save': 'Enregistrer',
  'consent.privacyLink': 'Lire notre politique de confidentialité',
  'consent.cat.necessary.label': 'Strictement nécessaires',
  'consent.cat.necessary.desc':
    'Authentification, session, sécurité. Requis pour utiliser SmartAnalyst — toujours actifs.',
  'consent.cat.analytics.label': 'Analytics',
  'consent.cat.analytics.desc':
    'Nous aide à comprendre comment l’app est utilisée pour l’améliorer. Agrégé, jamais revendu.',
  'consent.cat.marketing.label': 'Marketing',
  'consent.cat.marketing.desc':
    'Permet de mesurer nos campagnes publicitaires. Désactivé par défaut.',

  // ━━━ Tracking (bloc SmartTag dans Settings) ━━━
  'tracking.writeKey.label': 'Clé d’écriture',
  'tracking.writeKey.body':
    'Utilisée par le snippet de tracking pour identifier ton espace. Publique — peut être exposée côté client sans risque.',
  'tracking.snippet.title': 'Snippet à installer',
  'tracking.snippet.body':
    'Colle-le dans le <head> de chaque page à tracker. 1,5 Ko gzippé, asynchrone, zéro cookie.',
  'tracking.cta.viewInstall': 'Guide d’installation complet',
  'tracking.cta.viewLive': 'Voir le live',
  'common.copy': 'Copier',
  'common.copied': 'Copié',

  // ━━━ Live polish (filtres + sparkline) ━━━
  'live.filter.all': 'Tout',
  'live.filter.pageview': 'Vues',
  'live.filter.click': 'Clics',
  'live.filter.error': 'Erreurs',
  'live.filter.session_start': 'Sessions',
  'live.filter.custom': 'Custom',
  'live.chart.title': 'Pages vues par minute',
  'live.chart.empty': 'En attente de données…',

  // ━━━ Guide d’installation (/tracking/install) ━━━
  'install.kicker': 'Installation',
  'install.title': 'Installe ton tag en 3 étapes.',
  'install.subtitle':
    '2 minutes. Compatible avec n’importe quel CMS — WordPress, Shopify, Webflow, Ghost, ou HTML brut.',
  'install.step1.title': '1. Copie ton snippet',
  'install.step1.body':
    'Il est déjà rempli avec la clé d’écriture de ton espace. Tu n’as qu’à le copier.',
  'install.step2.title': '2. Colle-le dans le <head> de ton site',
  'install.step2.body': 'Choisis ta plateforme pour les instructions exactes :',
  'install.step2.tab.html': 'HTML brut',
  'install.step2.tab.wordpress': 'WordPress',
  'install.step2.tab.shopify': 'Shopify',
  'install.step2.tab.webflow': 'Webflow',
  'install.step2.tab.ghost': 'Ghost',
  'install.step2.html.body':
    'Ouvre les fichiers de ton site et colle le snippet juste avant </head> sur chaque page (ou dans ton template / layout partagé).',
  'install.step2.wordpress.body':
    'WordPress admin → Apparence → Éditeur de fichiers du thème → header.php → coller juste avant </head>. Ou via le plugin gratuit « Insert Headers and Footers » : Réglages → Insert Headers and Footers → champ « Scripts in Header ».',
  'install.step2.shopify.body':
    'Shopify admin → Boutique en ligne → Thèmes → Actions → Modifier le code → Layout → theme.liquid → coller juste avant </head>. Enregistrer.',
  'install.step2.webflow.body':
    'Webflow → Project Settings → Custom Code → Head Code → coller le snippet → Save → republier le site.',
  'install.step2.ghost.body':
    'Ghost admin → Settings → Code injection → Site Header → coller le snippet → Save.',
  'install.step3.title': '3. Vérifie que ça marche',
  'install.step3.body':
    'Ouvre ton site dans un nouvel onglet et navigue. Reviens ensuite ici et ouvre la page Live — tu dois voir des événements apparaître en quelques secondes.',
  'install.step3.cta': 'Ouvrir la page Live',
  'install.faq.title': 'Questions fréquentes',
  'install.faq.q1': 'Faut-il un bandeau cookies ?',
  'install.faq.a1':
    'Non. Les événements sont liés à un ID de session stocké en sessionStorage (effacé à la fermeture de l’onglet). Le tag ne pose aucun cookie, ne suit pas entre sessions ou entre sites, et n’a pas besoin de consentement pour l’analytique selon les lignes directrices CNIL / ePrivacy. Si tu charges aussi Google Analytics ou Meta Pixel à côté, eux nécessitent toujours du consentement.',
  'install.faq.q2': 'Est-ce que ça ralentit mon site ?',
  'install.faq.a2':
    'Le script est asynchrone (non-bloquant) et pèse 1,5 Ko gzippé — moins qu’un favicon. Les événements sont envoyés via navigator.sendBeacon pour ne jamais retarder la fermeture de la page. Aucun impact sur les Core Web Vitals.',
  'install.faq.q3': 'Quelles données sont collectées ?',
  'install.faq.a3':
    'URLs des pages (path + clés de query uniquement, pas les valeurs), origine du référent uniquement, sélecteurs de clics (pas le contenu texte), et tout événement custom envoyé via Smartanalyst("track", ...). Les emails et numéros de téléphone dans les propriétés custom sont retirés côté serveur en filet de sécurité.',
  'install.faq.q4': 'Puis-je envoyer des événements custom ?',
  'install.faq.a4':
    'Oui — Smartanalyst("track", "signup_completed", { plan: "pro" }). Les valeurs doivent être des strings, nombres ou booléens. Max 20 propriétés par événement, strings tronquées à 200 caractères.',
  'install.faq.q5': 'Comment désactiver les pageviews auto (SPA) ?',
  'install.faq.a5':
    'Passe disableAutoPageview: true à Smartanalyst("init", { ... }) puis appelle Smartanalyst("page") manuellement à chaque changement de route.',

  // ━━━ Audit (SEO / GEO on-demand) ━━━
  'nav.audit': 'Audit',
  'audit.kicker': 'Audit',
  'audit.title': 'Audite ton site en 5 secondes.',
  'audit.subtitle':
    'SEO + GEO (visibilité dans les moteurs IA) + Performance (Core Web Vitals) + scoring IA (Claude évalue la citabilité). 30+ checks en moins de 30 secondes.',
  'audit.form.url.label': 'URL à auditer',
  'audit.form.url.placeholder': 'https://ton-site.com',
  'audit.form.run': 'Lancer l’audit',
  'audit.form.running': 'Audit en cours…',
  'audit.form.runningHint': 'Chargement de la page et analyse — 5 à 10 secondes.',
  'audit.score.label': 'Score global',
  'audit.score.outOf': '/ 100',
  'audit.summary.pass': 'OK',
  'audit.summary.warn': 'À surveiller',
  'audit.summary.fail': 'À corriger',
  'audit.summary.info': 'Info',
  'audit.section.seo': 'Findings SEO',
  'audit.section.geo': 'Findings GEO (visibilité IA)',
  'audit.section.perf': 'Performance (Core Web Vitals)',
  'audit.section.ai': 'AI readiness (citation par les LLMs)',
  'audit.subscore.seo': 'SEO',
  'audit.subscore.geo': 'GEO',
  'audit.subscore.perf': 'Perf',
  'audit.subscore.ai': 'IA',
  'audit.cwv.title': 'Core Web Vitals',
  'audit.skipped.title': 'Analyzer désactivé',
  'audit.ai.valueProp': 'Promesse',
  'audit.ai.citation': 'Citable',
  'audit.ai.qa': 'Structure Q/R',
  'audit.ai.jargon': 'Jargon (moins c’est mieux)',
  'audit.ai.coherence': 'Cohérence titre',
  'audit.ai.strengths': 'Forces',
  'audit.ai.weaknesses': 'Faiblesses',
  'audit.ai.verdict': 'Verdict IA',

  // ━━━ Smart tag dashboard (onglets Analytics / SEO / GEO / Perf / AI) ━━━
  'dash.tab.analytics': 'Analytics',
  'dash.tab.seo': 'SEO',
  'dash.tab.geo': 'GEO',
  'dash.tab.perf': 'Performance',
  'dash.tab.ai': 'Lecture IA',
  'dash.audit.lastRun': 'Dernier audit',
  'dash.audit.rerun': 'Relancer l’audit',
  'dash.audit.rerunning': 'En cours…',
  'dash.audit.openFull': 'Voir le rapport complet',
  'dash.audit.findings': 'Findings',
  'dash.audit.sectionMissing':
    'Cette section n’est pas disponible dans le dernier audit. Relance pour la générer.',
  'dash.audit.perfSkipped': 'L’analyzer Performance nécessite une clé Google PageSpeed côté API.',
  'dash.audit.aiSkipped': 'L’analyzer Lecture IA nécessite une clé Anthropic côté API.',
  'dash.audit.empty.title': 'Lance ton premier audit',
  'dash.audit.empty.body':
    'Rapport sur 4 axes (SEO, recherche IA, performance, citabilité) sur n’importe quelle URL en moins de 30 secondes.',
  'dash.audit.empty.placeholder': 'https://ton-site.com',
  'dash.audit.empty.cta': 'Lancer l’audit',
  'dash.audit.empty.running': 'Audit en cours…',
  'audit.severity.pass': 'OK',
  'audit.severity.warn': 'À voir',
  'audit.severity.fail': 'Fail',
  'audit.severity.info': 'Info',
  'audit.findings.recommendation': 'Recommandation',
  'audit.history.title': 'Audits récents',
  'audit.history.empty': 'Pas encore d’audit — lance le premier ci-dessus.',
  'audit.history.open': 'Ouvrir',
  'audit.error.invalid_url': 'Cette URL n’a pas l’air valide. Utilise https://example.com.',
  'audit.error.scrape_failed':
    'On n’arrive pas à joindre le site (timeout, DNS, ou erreur SSL). Vérifie l’URL et réessaie.',
  'audit.error.generic': 'Quelque chose a planté pendant l’audit. Réessaie.',
  'audit.finalUrl': 'URL finale après redirections',
  'audit.httpStatus': 'Code HTTP',
  // ━━━ ErrorBoundary ━━━
  'error.boundary.badge': 'Erreur inattendue',
  'error.boundary.title': "Quelque chose s'est mal passé.",
  'error.boundary.body':
    "Le problème a été noté. Rafraîchis la page ou reviens à l'accueil pour continuer.",
  'error.boundary.refresh': 'Rafraîchir',
  'error.boundary.home': "Retour à l'accueil",
}

export const STRINGS = { en, fr }
